import { PlanogramRenderer } from "./PlanogramRenderer.js";
import { PlanogramCamera } from "./PlanogramCamera.js";
import { PlanogramContainerAttributes, PlanogramRendererResources } from './Resources.js';
import * as THREE from 'three';
import { PlanogramObject, PlanogramObjectShaderDataManager } from "./PlanogramObject.js";
import { PlanogramPosition } from "./PlanogramPosition.js";
import { DragArray, SnapDecoration, AnchorData } from "./DragArray.js";
import { PlanogramBlockingPosition } from "./PlanogramBlockingPosition.js";
import { PlanogramBlocking } from "./PlanogramBlocking.js";
import { PlanogramBlockingDivider } from "./PlanogramBlockingDivider.js";
import { PlanogramComponent } from "./PlanogramComponent.js";
import { PlanogramCameraFrame } from "./PlanogramCameraFrame.js";
import { PlanogramSubcomponent } from "./PlanogramSubcomponent.js";
import { PlanogramImageController, PlanogramImage } from "./PlanogramImage.js";
import { PlanogramTextController } from "./PlanogramText.js";
import { PlanogramLabelController } from "./PlanogramLabelController.js";
import { PlanogramComponentTransformController } from "./PlanogramComponentTransformController.js";
import { FloatingGrid } from "./FloatingGrid.js";

export class PlanogramContainer extends HTMLElement {

    //#region Fields and Attributes

    //#region Static Attributes
    static Attributes = PlanogramContainerAttributes.Attributes;
    static customEvents = {
        NewBox: "newbox",
        ContextMenu: "contextmenu"
    }
    static PasteDistanceFromCamera = 150;
    //#endregion

    //#region Private Fields

    //#region Selection and Dragging Fields
    /** @type { PlanogramObject|PlanogramBlockingDivider|null } */
    #targetOnDown;
    /**@type {THREE.Vector2|null} */
    #downPosition = null;
    #plane = new THREE.Plane();
    #offset = new THREE.Vector3();
    #intersection = new THREE.Vector3();
    #worldPosition = new THREE.Vector3();
    #inverseMatrix = new THREE.Matrix4();
    #raycaster = new THREE.Raycaster();
    #dragVector = new THREE.Vector3();
    #dragStartVector = new THREE.Vector3();
    #dividerDragOffset = null;
    #dividerOutline = null;
    #dividerOutlineTarget = null;
    #intersectTarget = new THREE.Vector3();

    //#region Rotation Gizmo Fields
    /**@type {PlanogramComponentTransformController|null} */
    #rotationGizmo = null;
    #rotating = false;
    /**@type {"x"|"y"|"z"|null} */
    #rotateAxis = null;
    #rotateNormal = new THREE.Vector3();
    #rotateCentre = new THREE.Vector3();
    #rotateU = new THREE.Vector3();
    #rotateV = new THREE.Vector3();
    #rotatePlane = new THREE.Plane();
    #rotatePoint = new THREE.Vector3();
    #rotatePrevAngle = 0;
    #rotateDelta = 0;
    #rotateAppliedDelta = 0;
    /**@type {THREE.InstancedMesh|null} */
    #rotatePreview = null;
    get isRotating() { return this.#rotating; }

    //#region Resize Gizmo Fields
    #resizing = false;
    #resizeCentre = new THREE.Vector3();
    #resizeCorner = new THREE.Vector3();
    #resizeAnchor = new THREE.Vector3();
    #resizeGrab = new THREE.Vector3();
    #resizeOldExtent = new THREE.Vector3();
    #resizeScaleVec = new THREE.Vector3(1, 1, 1);
    #resizePlane = new THREE.Plane();
    #resizePoint = new THREE.Vector3();
    /**@type {THREE.InstancedMesh|null} */
    #resizePreview = null;
    get isResizing() { return this.#resizing; }
    //#endregion
    //#endregion


    // Pointer event tracking for divider dragging
    #pointerDragging = false;
    #activePointerId = null;
    #pointerDragTarget = null;

    static #DEFAULT_BOX_SIZE = 10;

    // Add-box drawing state: 0 = idle, 1 = footprint drag, 2 = height
    #addBoxStage = 0;
    #addBoxPointerId = null;
    /**@type {THREE.Vector2|null} */
    #boxDownScreen = null;
    #boxStartLocal = new THREE.Vector3();
    #boxEndLocal = new THREE.Vector3();
    #boxFloorWorldY = 0;
    #boxHeight = 0;
    /**@type {"x"|"y"|"z"|null} */
    #addBoxOrthoAxis = null;
    #addBoxOrthoAnchor = 0;
    #addBoxOrthoDirection = 1;
    /**@type {THREE.LineSegments|null} */
    #boxPreview = null;
    //#endregion

    //#region Tab Item Fields
    /**@type {HTMLDivElement} */
    #tabItem = null;
    get tabItem() { return this.#tabItem; }
    /**@type {HTMLButtonElement} */
    #closebutton = null;
    /**@type {HTMLSpanElement} */
    #text = null;
    get cameraFrame() {
        if (!this.groupFrameId) return null;
        const frames = [...this.renderer.querySelectorAll('planogram-camera-frame')];
        return frames.find(f => f.uid === this.groupFrameId);
    }
    //#endregion

    #firstRender = true;
    /**@type {THREE.Scene} */
    #scene = new THREE.Scene();
    /**@type {THREE.Group} */
    #group = new THREE.Group();
    /** @type {THREE.ShaderMaterial} */
    #subcomponentShader = null;
    /** @type {THREE.ShaderMaterial} */
    #unitShader = null;
    /** @type {PlanogramImageController} */
    #subcomponentImageController = null;
    /** @type {PlanogramImageController} */
    #unitImageController = null;
    /** @type {THREE.ShaderMaterial} */
    #textShader = null;
    /** @type {PlanogramTextController} */
    #textController = null;
    /** @type {PlanogramLabelController} */
    #labelController = null;
    /** @type {PlanogramObjectShaderDataManager} */
    #objectDataManager = null;

    get #targetDraggable() {
        // Allow blocking dividers to be draggable even when not selected
        if (!this.#targetOnDown) return null;
        if (this.#targetOnDown instanceof PlanogramBlockingDivider) return this.#targetOnDown;
        return this.#targetOnDown.selected ? this.#targetOnDown : null;
    }
    get isDraggable() { return this.#targetDraggable !== null; }
    #uid = `planogram-tab-${Math.random().toString(36).substring(2, 9)}`;
    get uid() { return this.#uid; }

    //#endregion

    //#region Public Fields
    get group() { return this.#group; }
    get scene() { return this.#scene; }
    get boundingBox() {
        const components = this.scene.getObjectsByProperty("name", PlanogramComponent.name).map(x => x.userData.boundingBox);
        const positions = this.scene.getObjectsByProperty("name", PlanogramPosition.name).map(x => x.userData.boundingBox);
        const combined = [...components, ...positions].reduce((acc, x) => acc.union(x.clone()), new THREE.Box3());
        return combined;
    }
    get planogramBox() {
        var vector = new THREE.Vector3(this.width, this.height, this.depth);
        const boundingBox = this.boundingBox;
        // if(vector.equals(new THREE.Vector3(0,0,0)))
        vector = boundingBox.getSize(new THREE.Vector3());
        vector.setZ(vector.z + 2);
        return new THREE.Box3().setFromCenterAndSize(boundingBox.getCenter(new THREE.Vector3()), vector);
    }
    /**@type {PlanogramCamera[]} */
    get activeCameras() {
        if (!this.containerId) return [];
        const renderer = PlanogramRenderer.get(this);
        if (!renderer) return [];
        return [...renderer.querySelectorAll(`planogram-camera[container-id='${this.containerId}']`)].filter(x => x.active);
    }
    /**@type {PlanogramCamera|null} */
    get focusedCamera() {
        if (!this.containerId) return null;
        const renderer = PlanogramRenderer.get(this);
        if (!renderer) return null;
        return renderer.querySelector(`planogram-camera[container-id='${this.containerId}'][focused]`) || null;
    }
    /**@type {PlanogramToolBar|null} */
    get toolBar() { return this.querySelector("planogram-toolbar"); }
    get subComponentShader() { return this.#subcomponentShader; }
    get subComponentImageController (){ return this.#subcomponentImageController; }
    get unitShader() { return this.#unitShader; }
    get unitImageController (){ return this.#unitImageController; }
    get textShader() { return this.#textShader; }
    get textController() { return this.#textController; }
    get labelController() { return this.#labelController; }

    //#endregion


    //#region Attribute Properties
    get containerId() { return this.getAttribute(PlanogramContainer.Attributes.ContainerId) || null; }
    get planogramName() { return this.getAttribute(PlanogramContainer.Attributes.PlanogramName) || "Planogram"; }
    get imagesEnabled() { return this.hasAttribute(PlanogramContainer.Attributes.ImagesEnabled); }
    get hideChestWalls() { return this.hasAttribute(PlanogramContainer.Attributes.HideChestWalls); }
    get autoZoom() { return this.hasAttribute(PlanogramContainer.Attributes.AutoZoom); }
    get width() { return Number(this.getAttribute(PlanogramContainer.Attributes.Width)) || 0; }
    get height() { return Number(this.getAttribute(PlanogramContainer.Attributes.Height)) || 0; }
    get depth() { return Number(this.getAttribute(PlanogramContainer.Attributes.Depth)) || 0; }
    get showPositionsThroughBlocking() { return this.hasAttribute(PlanogramContainer.Attributes.ShowPositionsThroughBlocking); }
    get groupName() { return this.getAttribute(PlanogramContainer.Attributes.GroupName) || null; }
    get groupFrameId() { return this.getAttribute(PlanogramContainer.Attributes.GroupFrameId) || null; }
    get groupFrameIndex() { return Number(this.getAttribute(PlanogramContainer.Attributes.GroupFrameIndex)) || -1; }
    get groupFocused() { return this.hasAttribute(PlanogramContainer.Attributes.GroupFocused); }
    get componentTransform() { return this.hasAttribute(PlanogramContainer.Attributes.ComponentTransform); }
    get addBox() { return this.hasAttribute(PlanogramContainer.Attributes.AddBox); }
    //#endregion



    /**@type {DragArray} */
    dragArrayModel = null;

    /**@type {SnapDecoration} */
    snapDecoration = null;

    static get observedAttributes() {
        return [...Object.values(PlanogramContainer.Attributes)];
    }
    //#endregion

    //#endregion
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        
        this.#subcomponentShader = PlanogramRendererResources.SubcomponentShader();
        this.#subcomponentImageController = new PlanogramImageController(this.#subcomponentShader);
        this.#unitShader = PlanogramRendererResources.UnitShader();
        this.#unitImageController = new PlanogramImageController(this.#unitShader);
        this.#textShader = PlanogramRendererResources.TextShader();
        this.#textController = new PlanogramTextController(this.#textShader);
        this.#labelController = new PlanogramLabelController(this.#textShader);
        this.#objectDataManager = new PlanogramObjectShaderDataManager(this.#subcomponentShader);
    }

    //#region Scene Methods
    #setUpScene() {
        this.#scene = new THREE.Scene();
        this.#scene.background = new THREE.Color(0xffffff);
        this.#group = new THREE.Group();
        this.#group.name = this.planogramName;
        this.#scene.add(this.#group);
        
        // this.querySelectorAll("planogram-camera").forEach(x=>this.attachGrid(x)); 

    }

    updateObjectDataManager() {
        this.#objectDataManager.updateData(this);
    }

    /**
     * 
     * @param {string} type 
     * @returns {PlanogramImageController} 
     */
    getImageController(type) {
        if (type === PlanogramImage.type.Subcomponent)
            return this.#subcomponentImageController;
        return this.#unitImageController;
    }

    addToScene(item) {
        this.#group.add(item);
    }

    /**
     * 
     * @param {string} uid 
     * @returns {PlanogramPosition|undefined}
     */
    getPlanogramPositionFromUid = (uid) => [...this.querySelectorAll(`planogram-position`)].find(x => x.uid === uid);
    /**
     * 
     * @param {string} uid 
     * @returns {PlanogramComponent|undefined}
     */
    getPlanogramSubComponentFromUid = (uid) => [...this.querySelectorAll(`planogram-component`)].find(x => x.uid === uid);

    /**
     * 
     * @param {string} uid 
     * @returns {PlanogramObject|undefined}
     */
    getPlanogramObjectFromUid = (uid) => {
        var names = [];
        var renderer = PlanogramRenderer.get(this);
        if (!renderer.ignorePositions) names.push("planogram-position");
        if (!renderer.ignoreSubcomponents) names.push("planogram-component");
        if (renderer.includeBlocking) names.push("planogram-blocking");
        return [...this.querySelectorAll(names.join(","))].find(x => x.uid === uid);
    };
    //#endregion

    //#region Spacial Calculation Methods

    /**
     * 
     * @param {PlanogramObject} target 
     * @param {number} clampDetection 
     * @param {THREE.Vector3} position
     * @returns {THREE.Vector3}
     */
    #getNearestObject(position = null) {
        const target = this.dragArrayModel?.target;
        const ignoreIds = this.dragArrayModel?.planogramObjects?.map(x => x.uid) ?? [];
        const subComponents = this.#group.getObjectsByProperty("name", PlanogramComponent.name);
        const positions = this.#group.getObjectsByProperty("name", PlanogramPosition.name);
        const targetMb = target.mainBlockBoundingBox;
        const targetRealBox = (targetMb && !targetMb.isEmpty()) ? targetMb : target.groupBoundingBox;
        const targetBox = new THREE.Box3().setFromCenterAndSize(
            (position ? position : new THREE.Vector3(target.x, target.y, target.z)),
            targetRealBox.getSize(new THREE.Vector3()));
        const closestFaces = [...subComponents, ...positions].filter(x => x instanceof THREE.Group && !ignoreIds.includes(x.userData.id));
        return this.fromNearest(targetBox, closestFaces);
    }

    snapToUnit(position, unit) {
        return new THREE.Vector3(
            Math.round(position.x / unit) * unit,
            Math.round(position.y / unit) * unit,
            Math.round(position.z / unit) * unit
        );
    }

    static getNormalFromFace(face) {
        if (face === PlanogramObject.face.Left) return new THREE.Vector3(-1, 0, 0);
        if (face === PlanogramObject.face.Right) return new THREE.Vector3(1, 0, 0);
        if (face === PlanogramObject.face.Top) return new THREE.Vector3(0, 1, 0);
        if (face === PlanogramObject.face.Bottom) return new THREE.Vector3(0, -1, 0);
        if (face === PlanogramObject.face.Front) return new THREE.Vector3(0, 0, 1);
        if (face === PlanogramObject.face.Back) return new THREE.Vector3(0, 0, -1);
    }

    static getFaceFromNormal(normal) {
        if (normal.equals(new THREE.Vector3(-1, 0, 0))) return PlanogramObject.face.Left;
        if (normal.equals(new THREE.Vector3(1, 0, 0))) return PlanogramObject.face.Right;
        if (normal.equals(new THREE.Vector3(0, 1, 0))) return PlanogramObject.face.Top;
        if (normal.equals(new THREE.Vector3(0, -1, 0))) return PlanogramObject.face.Bottom;
        if (normal.equals(new THREE.Vector3(0, 0, 1))) return PlanogramObject.face.Front;
        if (normal.equals(new THREE.Vector3(0, 0, -1))) return PlanogramObject.face.Back;
    }

    /**
     * 
     * @param {THREE.Box3} target 
     * @param {THREE.Object3D[]} objects 
     * @returns {THREE.Vector3}
     */
    fromNearest(target, objects) {
        const renderer = PlanogramRenderer.get(this);
        const constrainToFace = renderer?.ctrlKey ?? false;
        var constrainVector = constrainToFace;
        const clampDetection = renderer.snappingDistance;
        const gap = renderer.hangingGap;
        const boxPosition = target.getCenter(new THREE.Vector3());
        const boxSize = target.getSize(new THREE.Vector3());
        const objectsWithFaces = objects.flatMap(x => Object.values(PlanogramObject.face).map(face => { return { face, object: x }; }));

        const { volume, newSize, faceBox, objectBox, n, c, normal, object } = objectsWithFaces.reduce((i, o) => {
            const { face, object } = o;
            const normal = PlanogramContainer.getNormalFromFace(face);
            const size = new THREE.Box3().setFromObject(object).getSize(new THREE.Vector3());
            const c = normal.toArray().reduce((a, c, i) => { return (c !== 0 && a === -1) ? i : a; }, -1);
            const n = normal.getComponent(c);
            const faceBox = new THREE.Box3().setFromCenterAndSize(
                object.position.clone().add(new THREE.Vector3().setComponent(c, ((size.getComponent(c) + clampDetection) / 2) * n)),
                size.setComponent(c, clampDetection));
            const intersectedBox = faceBox.clone().intersect(target);
            const newSize = intersectedBox.getSize(new THREE.Vector3());
            const volume = newSize.x * newSize.y * newSize.z;
            const planObjectForBox = this.getPlanogramObjectFromUid(object.userData.id);
            const objectMb = planObjectForBox.mainBlockBoundingBox;
            const objectBox = ((objectMb && !objectMb.isEmpty()) ? objectMb : planObjectForBox.groupBoundingBox).clone();
            if (newSize.toArray().every(x => x !== 0) && volume > i.volume)
                return { volume, object, newSize, faceBox, objectBox, n, c, normal };
            return i;
        }, { volume: 0, newSize: new THREE.Vector3(), object: new THREE.Object3D(), faceBox: new THREE.Box3(), objectBox: new THREE.Box3(), n: 0, c: 0, normal: new THREE.Vector3() });

        if (volume === 0) return boxPosition;

        const planObject = this.getPlanogramObjectFromUid(object.userData.id);
        const hasAnchors = planObject?.snapAnchors.length > 0;

        const applyGap = !hasAnchors && newSize.getComponent(c) < (clampDetection - gap) ? gap : 0;
        const transformedVector = boxPosition.clone().setComponent(c, new THREE.Vector3().copy(n > 0 ? faceBox.min : faceBox.max).
            add(new THREE.Vector3().setComponent(c, ((boxSize.getComponent(c) / 2) + applyGap) * n)).getComponent(c));
        const projectedBox = this.dragArrayModel.boxes.reduce((a, c) => a.union(c), new THREE.Box3()).translate(transformedVector);
        // const anchor = planObject?.getNearestAnchor( normal, transformedVector );
        // if(anchor){
        //     if(anchor.forceConstraint)constrainVector = true;
        //     transformedVector.copy(anchor.getFacePosition( normal, transformedVector,boxSize ));
        // }

        this.setSnapDecorator(objectBox, normal);
        return constrainVector ? this.constrainToFace(transformedVector, boxSize, objectBox, normal) : transformedVector;
    }

    #intersectBox = null;
    /**
     * 
     * @param {THREE.Box3} target 
     * @param {THREE.Intersection} intersection 
     * @returns {THREE.Vector3}
     */
    fromIntersection(target, intersection) {
        const constrainToFace = PlanogramRenderer.get(this)?.ctrlKey ?? false;
        var constrainVector = constrainToFace;
        const boxSize = target.getSize(new THREE.Vector3());
        const affectedComponent = intersection.normal.toArray().reduce((a, c, i) => { return (c !== 0 && a === -1) ? i : a; }, -1);
        const unAffectedComponents = [0, 1, 2].filter(x => x !== affectedComponent);
        const normalComponent = intersection.normal.getComponent(affectedComponent);
        const offset = this.#offset.clone();
        var transformedVector = intersection.point.clone();
        const projectedBox = this.dragArrayModel.boxes.reduce((a, c) => a.union(c), new THREE.Box3()).translate(transformedVector);
        const affectFace = (normalComponent < 0 ? projectedBox.max : projectedBox.min).getComponent(affectedComponent);
        const distanceToFace = affectFace - transformedVector.getComponent(affectedComponent);
        transformedVector.setComponent(affectedComponent, transformedVector.getComponent(affectedComponent) - distanceToFace).
            sub(unAffectedComponents.reduce((a, c) => a.setComponent(c, offset.getComponent(c)), new THREE.Vector3())).
            add(new THREE.Vector3(0, affectedComponent != 1 ? offset.y : 0, 0));
        const planObject = this.getPlanogramObjectFromUid(intersection.object.userData.id);
        const anchor = planObject?.getNearestAnchor(intersection.normal, transformedVector);
        if (anchor) {
            if (anchor.forceConstraint) constrainVector = true;
            const planObjectMb = planObject.mainBlockBoundingBox;
            const planObjectBox = (planObjectMb && !planObjectMb.isEmpty()) ? planObjectMb : planObject.groupBoundingBox;
            transformedVector.copy(anchor.getFacePosition(planObjectBox, intersection.normal, transformedVector, boxSize));
        }
        this.setSnapDecorator(new THREE.Box3().setFromObject(intersection.object), intersection.normal);
        return constrainVector ? this.constrainToFace(transformedVector, boxSize, new THREE.Box3().setFromObject(intersection.object), intersection.normal) : transformedVector;
    }

    /**
     * 
     * @param {PlanogramObject} target 
     * @param {THREE.Intersection} intersection 
     */
    fromIntersectionUV(target, intersection) {

    }

    setSnapDecorator(box, normal) {
        if (this.snapDecoration) this.removeSnapDecorator();
        this.snapDecoration = new SnapDecoration(box, normal);
        this.group.add(this.snapDecoration);
    }


    removeSnapDecorator() {
        if (this.snapDecoration) {
            this.snapDecoration.geometry.dispose();
            this.snapDecoration.material.dispose();
            this.snapDecoration.removeFromParent();
            this.snapDecoration = null;
        }
    }
    /**
     * 
     * @param {THREE.Vector3} transformedVector 
     * @param {THREE.Vector3} box1Size 
     * @param {THREE.Box3} box2 
     * @param {THREE.Vector3} normal 
     * @returns {THREE.Vector3}
     */
    constrainToFace(transformedVector, box1Size, box2, normal) {
        const affectedComponent = normal.toArray().reduce((a, c, i) => { return (c !== 0 && a === -1) ? i : a; }, -1);
        const box2Size = box2.getSize(new THREE.Vector3());
        const box2Centre = box2.getCenter(new THREE.Vector3());
        const projectedBox = this.dragArrayModel.boxes.reduce((a, c) => a.union(c), new THREE.Box3()).translate(transformedVector);
        const maxSpace = Array(3).fill(0).reduce((a, c, i) => a.setComponent(i,
            (box1Size.getComponent(i) > box2Size.getComponent(i)) ? ((box1Size.getComponent(i) - box2Size.getComponent(i)) * 2) + box2Size.getComponent(i) : box2Size.getComponent(i)),
            new THREE.Vector3());

        const newBounds = new THREE.Box3().setFromCenterAndSize(box2Centre, maxSpace);
        const constrainedVector = Array(3).fill(0).reduce((a, c, i) => {
            if (i === affectedComponent) a.setComponent(i, 0);
            else if (projectedBox.max.getComponent(i) > newBounds.max.getComponent(i))
                a.setComponent(i, -(projectedBox.max.getComponent(i) - newBounds.max.getComponent(i)));
            else if (projectedBox.min.getComponent(i) < newBounds.min.getComponent(i))
                a.setComponent(i, newBounds.min.getComponent(i) - projectedBox.min.getComponent(i));
            return a;
        }, new THREE.Vector3());

        return transformedVector.clone().add(constrainedVector);
    }


    //#endregion

    //#region Planogram Tab Methods
    #setUpTabItem() {

        if (!this.groupFrameId) {
            this.#tabItem?.remove();
            this.#tabItem = null;
            return;
        }

        if (this.#tabItem) return;

        const topHighlight = "highlight-top";
        const bottomHighlight = "highlight-bottom";
        const leftHighlight = "highlight-left";
        const rightHighlight = "highlight-right";
        const dragThreshold = 5;
        const tabItem = document.createElement("div");
        tabItem.classList.add("tabItem");
        tabItem.id = this.uid;
        tabItem.addEventListener("pointerdown", down => {
            if (this.#closebutton && this.#closebutton.contains(down.target)) return;
            /**@type {HTMLDivElement|undefined} */
            var firstContainer = null;
            /**@type {HTMLDivElement|undefined} */
            var firstTabBar = null;
            /**@type {HTMLDivElement|undefined} */
            var highlightedTabItem = null;

            const rootNode = tabItem.getRootNode();
            const rootRenderer = PlanogramRenderer.get(this);
            down.preventDefault();
            down.stopPropagation();
            tabItem.setPointerCapture(down.pointerId);

            /**@type {HTMLElement} */
            const ghostTab = document.createElement("div");
            ghostTab.classList.add("tabGhost");
            ghostTab.style.left = `${down.clientX}px`;
            ghostTab.style.top = `${down.clientY}px`;
            ghostTab.innerText = tabItem.innerText;
            /**@type {HTMLElement} */

            const tabControlRoot = rootNode.querySelector(".tabControl.root");
            const frames = [...rootRenderer.querySelectorAll('planogram-camera-frame')];
            const cameraFrame = frames.find(f => f.uid === this.groupFrameId);
            // Check if there are other leaf frames (frames with no child frames) that have cameras
            const noOtherCameras = [...rootRenderer.querySelectorAll(`planogram-camera-frame`)].
                filter(x => x.childFrames.length === 0 && x !== cameraFrame).
                flatMap(x => x.associatedCameras).length === 0;
            const siblingLength = !cameraFrame || (cameraFrame?.associatedCameras.length === 1 && noOtherCameras);

            const mousemove = move => {
                if (siblingLength || Math.abs(down.clientX - move.clientX) < dragThreshold && Math.abs(down.clientY - move.clientY) < dragThreshold)
                    return;
                if (!tabControlRoot.contains(ghostTab))
                    tabControlRoot.appendChild(ghostTab);
                rootNode.querySelectorAll(".tabContainer").forEach(x => x.classList.remove(topHighlight, bottomHighlight, leftHighlight, rightHighlight));
                const offset = tabControlRoot.getBoundingClientRect();
                tabItem.style.cursor = "grabbing";
                ghostTab.style.cursor = "grabbing";
                ghostTab.style.left = `${move.clientX - offset.x}px`;
                ghostTab.style.top = `${move.clientY - offset.y}px`;

                [...rootNode.querySelectorAll(".tabBar")].flat().flatMap(x => [...x.children]).forEach(x => x.classList.remove("dragLeft", "dragRight"));

                firstTabBar = [...rootNode.querySelectorAll(".tabBar")].find(x => {
                    const boundingBox = x.getBoundingClientRect();
                    return move.clientX > boundingBox.left && move.clientX < boundingBox.right && move.clientY > boundingBox.top && move.clientY < boundingBox.bottom
                });

                firstContainer = [...rootNode.querySelectorAll(".tabContainer")].
                    filter(x => ![...x.querySelectorAll('*')].some(y => y.classList.contains("tabContainer"))).find(x => {
                        const boundingBox = x.getBoundingClientRect();
                        return move.clientX > boundingBox.left && move.clientX < boundingBox.right && move.clientY > boundingBox.top && move.clientY < boundingBox.bottom
                    });

                // if this tabitem is the only tab item in the tabcontrol then we can't split it's own container
                if (firstContainer) {

                    const frame = this.getCameraFrameFromUID(firstContainer.getAttribute(PlanogramCameraFrame.UnobservedAttributes.AttachedFrame));
                    if (frame && frame.canSplit(this.#tabItem)) {
                        const isSibling = frame.isSiblingFrame(this.#tabItem);
                        const parentDirection = frame.parentElement.childFrameDirection;
                        const rect = firstContainer.getBoundingClientRect();
                        const verticalMid = rect.top + rect.height / 2;
                        const horizontalMid = rect.left + rect.width / 2;
                        const dx = move.clientX - horizontalMid;
                        const dy = move.clientY - verticalMid;
                        firstContainer.classList.remove(topHighlight, bottomHighlight, leftHighlight, rightHighlight);
                        if (isSibling) {
                            if (parentDirection === PlanogramCameraFrame.frameType.Horizontal)
                                firstContainer.classList.add(move.clientY > verticalMid ? bottomHighlight : topHighlight);
                            else if (parentDirection === PlanogramCameraFrame.frameType.Vertical)
                                firstContainer.classList.add(move.clientX > horizontalMid ? rightHighlight : leftHighlight);
                        } else {
                            if (Math.abs(dx) > Math.abs(dy)) {
                                if (!isSibling || isSibling && parentDirection === PlanogramCameraFrame.frameType.Vertical)
                                    firstContainer.classList.add(dx > 0 ? rightHighlight : leftHighlight);
                            } else {
                                if (!isSibling || isSibling && parentDirection === PlanogramCameraFrame.frameType.Horizontal)
                                    firstContainer.classList.add(dy > 0 ? bottomHighlight : topHighlight);
                            }
                        }

                    }
                } else if (firstTabBar) {
                    const childTabItems = [...firstTabBar.children].filter(x => x.classList.contains("tabItem") && x !== tabItem);
                    highlightedTabItem = childTabItems.find((x, i) => {
                        const itemRect = x.getBoundingClientRect();
                        const isRight = move.clientX >= itemRect.x + itemRect.width / 2 && (move.clientX < itemRect.right || childTabItems.length === i + 1);
                        const isLeft = move.clientX < itemRect.x + itemRect.width / 2 && move.clientX > itemRect.left;
                        const value = isRight && x.nextElementSibling !== tabItem ? 1 : isLeft && x.previousElementSibling !== tabItem ? -1 : 0;
                        if (value !== 0) x.classList.add(value === 1 ? "dragRight" : value === -1 ? "dragLeft" : "");
                        return value !== 0;
                    });
                }

            };
            const mouseup = up => {
                tabItem.removeEventListener("pointermove", mousemove);
                tabItem.removeEventListener("pointerup", mouseup);
                ghostTab.remove();
                up.stopPropagation();

                if (Math.abs(down.clientX - up.clientX) < dragThreshold && Math.abs(down.clientY - up.clientY) < dragThreshold) {
                    if (!this.active)
                        this.raiseClickEvent();
                    else if (this.active && !this.focused)
                        this.raiseFocusEvent();
                } else {
                    if (firstContainer) {
                        const frame = this.getCameraFrameFromUID(firstContainer.getAttribute(PlanogramCameraFrame.UnobservedAttributes.AttachedFrame));
                        const anchor = firstContainer.classList.contains(topHighlight) ? 0 :
                            firstContainer.classList.contains(bottomHighlight) ? 1 :
                                firstContainer.classList.contains(leftHighlight) ? 2 :
                                    firstContainer.classList.contains(rightHighlight) ? 3 : null;
                        frame.raiseDropEvent(anchor, this.id);

                    } else if (highlightedTabItem) {
                        const anchor = highlightedTabItem.classList.contains("dragLeft") ? 2 :
                            highlightedTabItem.classList.contains("dragRight") ? 3 : null;
                        const camera = this.getCameraFromUID(highlightedTabItem.id);
                        this.raiseDropEvent(anchor, camera.id);
                    }

                }

                [...rootNode.querySelectorAll(".tabBar")].flatMap(x => [...x.children]).forEach(x => x.classList.remove("dragLeft", "dragRight"));
                [...rootNode.querySelectorAll(".tabContainer")].forEach(x => x.classList.remove(topHighlight, bottomHighlight, leftHighlight, rightHighlight));
                tabItem.releasePointerCapture(down.pointerId);
                tabItem.style.cursor = "pointer";

            };
            tabItem.addEventListener("pointermove", mousemove);
            tabItem.addEventListener("pointerup", mouseup);
        });

        tabItem.addEventListener("contextmenu", e => {
            e.preventDefault();
            e.stopPropagation();
            this.raiseContextMenuEvent(e.clientX, e.clientY);
        });
        this.#tabItem = tabItem;
        this.setActiveAndFocused();
        this.#setTabItemText();
        this.setTabDraggable();
        this.#setUpCloseButton()
    }

    #setTabItemText() {
        if (!this.#text) {
            this.#text = document.createElement("span");
            this.#tabItem.appendChild(this.#text);
        }
        const planName = this.groupName ?? this.planogramName;
        this.#text.innerText = planName;
    }

    #setUpCloseButton() {
        if (!this.disableClose && !this.#closebutton) {
            const button = document.createElement("button");
            button.innerHTML = `<svg height="16px" width="16px" xmlns="http://www.w3.org/2000/svg">
                <line x1="4" y1="4" x2="12" y2="12"></line>
                <line x1="12" y1="4" x2="4" y2="12"></line>
            </svg>`;
            button.addEventListener("click", e => {
                e.preventDefault();
                e.stopPropagation();

                this.raiseCancelEvent();
            });
            this.#closebutton = button;
            this.#tabItem.appendChild(button);
        }
        if (!this.disableClose && !this.#tabItem.contains(this.#closebutton)) this.#tabItem.appendChild(this.#closebutton);
        else if (this.disableClose && this.#tabItem.contains(this.#closebutton)) this.#closebutton.remove();

    }

    setTabDraggable() {
        this.#tabItem.draggable = true;
    }

    setActiveAndFocused() {
        if (this.isConnected && this.#tabItem && this.#tabItem.parentElement)
            this.cameraFrame?.updateFocus();
        this.activeCameras.at(0)?.setActiveAndFocused();
    }
    toolBarAssigned() {

    }
    //#endregion

    //#region Drag interaction Methods

    // /**
    //  * 
    //  * @param {Number} x
    //  * @param {Number} y 
    //  * @returns {{id:string, position:{x:number,y:number,z:number}, container:PlanogramContainer|null, target:PlanogramObject|null}}
    //  */
    // getTargetUnderCursor( x, y ){

    //     const container = this.focusedCamera?.container;
    //     const intersections = this.#getRayIntersections( x, y );
    //     if(intersections === null)return null;

    //     /**
    //      * @type {{id:string, position:{x:number,y:number,z:number}, container:PlanogramContainer, target:PlanogramObject|null}[]}
    //      */
    //     var [ firstItem ] = intersections.map( intersect => {
    //         var current = intersect.object;
    //         let {x ,y, z} = intersect.point;
    //         if(Reflect.has(current.userData, "id")){
    //             const target = [...container.querySelectorAll("*")].find(x=>x.uid === current.userData.id);
    //             return { ...current.userData, ...{ position: {x, y, z} }, container:container, target:target };
    //         }
    //         return { ...current.userData, ...{ position: {x, y, z} }, container:container, target:null };
    //     }).
    //     filter(x=>{
    //         return x.target instanceof PlanogramPosition && !this.ignorePositions || x.target instanceof PlanogramSubcomponent && !this.ignoreSubcomponents;
    //     }).reduce((a,c) => !a.some( x => x.id == c.id ) ? a.concat([c]) : a, []);

    //     return firstItem;
    // }

    /**
     * Returns the translated cursor position based on the dock vector of the focused camera
     * @param {number} x 
     * @param {number} y 
     * @param {boolean} drag 
     * @returns {THREE.Vector2}
     */
    #getTranslatedCursor(x, y, drag = false) {

        if (!this.focusedCamera?.dockVector) return new THREE.Vector2(x, y);
        const dockVector = this.focusedCamera.dockVector.clone();

        if (drag) {
            dockVector.x = 0;
            dockVector.y = 0;
        }
        var translatedCursor = new THREE.Vector2(
            ((x - dockVector.x) / dockVector.z) * 2 - 1,
            -((y - dockVector.y) / dockVector.w) * 2 + 1
        );

        return translatedCursor;
    }
    /**
     * 
     * @param {number} x 
     * @param {number} y 
     * @returns {THREE.Intersection[]|[]}
     */
    #getRayIntersections(x, y) {
        if (!this.focusedCamera) return null;
        const translatedCursor = this.#getTranslatedCursor(x, y);
        if (!translatedCursor) return null;
        var raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(translatedCursor, this.focusedCamera.camera);
        return raycaster.intersectObjects(this.group.children) ?? [];
    }

    /**
    * 
    * @param {number} x 
    * @param {number} y 
    * @param {PlanogramObject[]} ignore 
    * @returns {THREE.Intersection|null}
    */
    #getFirstIntersection(x, y, ignore, ignoreNames = []) {
        const renderer = PlanogramRenderer.get(this);

        const interactions = [];
        if (renderer.ignorePositions === false && !ignoreNames.includes(PlanogramPosition.name)) interactions.push(PlanogramPosition.name);
        if (renderer.ignoreSubcomponents === false && !ignoreNames.includes(PlanogramComponent.name)) interactions.push(PlanogramComponent.name);
        if (renderer.includeBlocking && !ignoreNames.includes(PlanogramBlocking.name)) interactions.push(PlanogramBlocking.name);
        if (interactions.length === 0) return null;

        const ignoreIds = ignore?.map(x => x.uid) ?? [];
        const intersections = this.#getRayIntersections(x, y);
        const [first] = intersections.filter(x => interactions.includes(x.object.name) && !ignoreIds.includes(x.object.userData.id));
        return first ?? null;
    }

    /**
     * Grid snapping step near the given world point, following the grid's zoom level of
     * detail: 1-unit cells while they are still readable on screen, 10-unit cells once
     * the camera is far enough out that the grid only shows the coarse lines.
     * @param {THREE.Vector3} point
     * @returns {number}
     */
    #gridSnapStep(point) {
        const cell = FloatingGrid.DEFAULT_CELL_SIZE;
        const camera = this.focusedCamera?.camera;
        const viewportHeight = this.focusedCamera?.dockVector?.w;
        if (!camera || !viewportHeight) return cell;

        const worldPerPixel = camera.isPerspectiveCamera
            ? 2 * camera.position.distanceTo(point) * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) / viewportHeight
            : (camera.top - camera.bottom) / camera.zoom / viewportHeight;
        const pixelsPerCell = cell / worldPerPixel;
        return pixelsPerCell < 8 ? cell * 10 : cell;
    }

    /** Snaps every component of the vector to the nearest grid point in place. */
    #snapToGrid(vector) {
        const step = this.#gridSnapStep(vector);
        return vector.set(
            Math.round(vector.x / step) * step,
            Math.round(vector.y / step) * step,
            Math.round(vector.z / step) * step
        );
    }

    /**
     *
     * @param {THREE.Vector2} cursorPosition
     */
    setDragArrayPosition(cursorPosition) {
        if (this.dragArrayModel) {
            this.clearDecorators();
            this.dragArrayModel.anchorData = null;
            this.dragArrayModel.eventTarget = this.dragArrayModel.target ?? null;
            const intersection = this.#getFirstIntersection(
                cursorPosition.x,
                cursorPosition.y,
                this.dragArrayModel?.planogramObjects,
                this.getIgnoreList(this.dragArrayModel?.target?.constructor.name, this.dragArrayModel?.type)
            );
            const box = this.dragArrayModel.focusBox.clone().translate(this.#dragVector);
            if (intersection && Reflect.has(intersection.object.userData, "id")) {
                const intersectedObject = this.getPlanogramObjectFromUid(intersection.object.userData.id);
                if (intersectedObject) {
                    if (this.dragArrayModel.type === "product") {
                        const rayCaster = new THREE.Raycaster();
                        rayCaster.setFromCamera(this.#getTranslatedCursor(cursorPosition.x, cursorPosition.y), this.focusedCamera.camera);
                        const worldBoundingBox = intersectedObject.groupBoundingBox.clone().applyMatrix4(intersectedObject.group.matrixWorld);
                        const intersectionPoint = rayCaster.ray.intersectBox(worldBoundingBox, this.#intersectTarget);
                        const faceNormal = intersectedObject.setDecoratorFromPoint(intersectionPoint);
                        this.dragArrayModel.anchorData = new AnchorData(faceNormal, intersectedObject);
                    } else {
                        this.dragArrayModel.eventTarget = this.dragArrayModel.target ?? intersectedObject;
                        const transformedVector = this.fromIntersection(box, intersection);
                        this.#dragVector.copy(transformedVector);
                    }
                }
                // }else if( renderer.snappingDistance > 0 && renderer.shiftKey ){
                //     const transformedVector = this.#getNearestObject( this.#dragVector );
                //     if(this.#dragVector.equals(transformedVector)) this.removeSnapDecorator();
                //     this.#dragVector.copy(transformedVector);
            } else {
                this.removeSnapDecorator();
            }
            const renderer = PlanogramRenderer.get(this);
            // Shift locks the movement to the dominant axis of the drag so far.
            if (renderer?.shiftKey) {
                const delta = this.#dragVector.clone().sub(this.#dragStartVector);
                const ax = Math.abs(delta.x), ay = Math.abs(delta.y), az = Math.abs(delta.z);
                if (ax >= ay && ax >= az) delta.set(delta.x, 0, 0);
                else if (ay >= az) delta.set(0, delta.y, 0);
                else delta.set(0, 0, delta.z);
                this.#dragVector.copy(this.#dragStartVector).add(delta);
            }
            // Ctrl snaps the dragged selection to the nearest grid point.
            if (renderer?.ctrlKey) {
                this.#snapToGrid(this.#dragVector);
            }
            this.dragArrayModel.position.copy(this.#dragVector);
            this.dragArrayModel.renderOrder = 9999;
        }
    }

    getIgnoreList(targetName) {
        const ignoreNames = [];
        if (targetName === PlanogramComponent.name || arguments[1] === "fixture") ignoreNames.push(PlanogramPosition.name);
        return ignoreNames;
    }

    setDividerPosition() {
        // Prefer pointer drag target when present (touch/pointer), otherwise use the mouse drag target
        const divider = this.#pointerDragTarget ?? this.#targetDraggable;
        if (!(divider instanceof PlanogramBlockingDivider)) return;
        const planBox = this.planogramBox.clone();
        const planSize = planBox.getSize(new THREE.Vector3());
        const planMin = planBox.min.clone();
        const length = divider.length;
        const dir = divider.direction;

        const pointerPos = this.#dragVector.clone();
        const offset = this.#dividerDragOffset ?? new THREE.Vector3();
        const dragCenter = pointerPos.clone().sub(offset);
        const existingNormX = divider.x;
        const existingNormY = divider.y;

        let originalCenterX, originalCenterY;
        if (dir === 'horizontal') {
            originalCenterX = planMin.x + (existingNormX * planSize.x) + (length * planSize.x / 2);
            originalCenterY = planMin.y + (existingNormY * planSize.y);
        } else {
            originalCenterX = planMin.x + (existingNormX * planSize.x);
            originalCenterY = planMin.y + (existingNormY * planSize.y) + (length * planSize.y / 2);
        }

        if (dir === 'horizontal') {
            dragCenter.x = originalCenterX;
        } else {
            dragCenter.y = originalCenterY;
        }

        let normX, normY;
        if (dir === 'horizontal') {
            normX = existingNormX;
            normY = (dragCenter.y - planMin.y) / planSize.y;
            normY = THREE.MathUtils.clamp(normY, 0, 1);
        } else {
            normX = (dragCenter.x - planMin.x) / planSize.x;
            normX = THREE.MathUtils.clamp(normX, 0, 1);
            normY = existingNormY;
            normY = THREE.MathUtils.clamp(normY, 0, 1 - length);
        }
        if (this.#dividerOutline && this.#dividerOutlineTarget === divider) {
            this.#dividerOutline.userData.normX = normX;
            this.#dividerOutline.userData.normY = normY;
            const center = new THREE.Vector3();
            if (dir === 'horizontal') {
                center.set(planMin.x + (normX * planSize.x) + (length * planSize.x / 2), planMin.y + (normY * planSize.y), planSize.z / 2);
            } else {
                center.set(planMin.x + (normX * planSize.x), planMin.y + (normY * planSize.y) + (length * planSize.y / 2), planSize.z / 2);
            }
            this.#dividerOutline.position.copy(center);
        }
    }

    /**
     * 
     * @param {PlanogramBlockingDivider} divider 
     */
    #createDividerOutline(divider) {
        try {
            const planBox = this.planogramBox.clone();
            const planSize = planBox.getSize(new THREE.Vector3());
            const dims = divider.getDimensions(planSize, true);
            const center = divider.getPosition(planSize);

            const geom = new THREE.BoxGeometry(dims.x, dims.y, planSize.z);
            const mat = new THREE.MeshBasicMaterial({ color: 0x999999, transparent: true, opacity: 0.9 });
            const outline = new THREE.Mesh(geom, mat);
            outline.position.copy(center);
            outline.renderOrder = 9999;
            outline.userData = { dividerUid: divider.uid, normX: divider.x, normY: divider.y };
            outline.layers.set(PlanogramRendererResources.DECORATOR_LAYER);
            this.group.add(outline);
            this.#dividerOutline = outline;
            this.#dividerOutlineTarget = divider;
        } catch (e) { console.warn('Failed to create divider outline', e); }
    }

    #removeDividerOutline(commit = false) {
        if (!this.#dividerOutline) return;
        if (commit && this.#dividerOutlineTarget) {
            /**@type {PlanogramBlockingDivider} */
            const divider = this.#dividerOutlineTarget;
            const outline = this.#dividerOutline;
            const planBox = this.planogramBox.clone();
            const planSize = planBox.getSize(new THREE.Vector3());
            const planMin = planBox.min.clone();
            const length = divider.length || 0;
            const pos = outline.position;
            let normX = 0, normY = 0;
            if (divider.direction === 'horizontal') {
                normX = (pos.x - planMin.x - (length * planSize.x / 2)) / planSize.x;
                normY = (pos.y - planMin.y) / planSize.y;
                normX = THREE.MathUtils.clamp(normX, 0, 1);
                normY = THREE.MathUtils.clamp(normY, 0, 1);
            } else {
                normX = (pos.x - planMin.x) / planSize.x;
                normY = (pos.y - planMin.y - (length * planSize.y / 2)) / planSize.y;
                normX = THREE.MathUtils.clamp(normX, 0, 1);
                normY = THREE.MathUtils.clamp(normY, 0, Math.max(0, 1 - length));
            }

            divider.raiseMoveEvent(Math.round(THREE.MathUtils.clamp(normX, 0, 1) * 1000), Math.round(THREE.MathUtils.clamp(normY, 0, 1) * 1000));

        }
        // dispose outline geometry/material and remove from scene
        try { this.#dividerOutline.geometry.dispose(); } catch (e) { }
        try { this.#dividerOutline.material.dispose(); } catch (e) { }
        this.#dividerOutline.removeFromParent();
        this.#dividerOutline = null;
        this.#dividerOutlineTarget = null;
    }

    clearDecorators() {
        this.scene.getObjectsByProperty("name", "Decorator").
            filter(x => x instanceof THREE.InstancedMesh).map(x => x.geometry).
            filter(x => x instanceof THREE.BoxGeometry).forEach(x => {
                if (!x.attributes.direction.array.slice(0, 2).every(x => x === 1)) {
                    x.attributes.direction.array.fill(0);
                    x.attributes.direction.needsUpdate = true;
                }
            });
    }

    #updateDragVectorFromCursor(x, y) {
        const translatedCursor = this.#getTranslatedCursor(x, y, true);
        this.#raycaster.setFromCamera(translatedCursor, this.focusedCamera.camera);

        if (!this.#raycaster.ray.intersectPlane(this.#plane, this.#intersection)) {
            return false;
        }

        this.#dragVector.copy(this.#intersection)
            .sub(this.#offset)
            .applyMatrix4(this.#inverseMatrix);
        return true;
    }

    #panCameraForDraggedTarget(targetPosition) {
        const camera = this.focusedCamera;
        const dragTarget = targetPosition?.clone();
        const dock = camera?.dockVector;

        if (!camera || !dragTarget || !dock || dock.z <= 0 || dock.w <= 0) {
            return false;
        }

        const projected = dragTarget.project(camera.camera);
        if (![projected.x, projected.y, projected.z].every(Number.isFinite)) {
            return false;
        }

        const viewportLeft = dock.x;
        const viewportTop = dock.y;
        const viewportRight = dock.x + dock.z;
        const viewportBottom = dock.y + dock.w;
        const thresholdX = Math.min(Math.max(dock.z * 0.15, 32), 96);
        const thresholdY = Math.min(Math.max(dock.w * 0.15, 32), 96);
        const maxPanPixels = 32;
        const screenX = viewportLeft + ((projected.x + 1) * 0.5 * dock.z);
        const screenY = viewportTop + ((1 - projected.y) * 0.5 * dock.w);

        let panX = 0;
        let panY = 0;

        if (screenX < viewportLeft + thresholdX) {
            panX = screenX - (viewportLeft + thresholdX);
        } else if (screenX > viewportRight - thresholdX) {
            panX = screenX - (viewportRight - thresholdX);
        }

        if (screenY < viewportTop + thresholdY) {
            panY = screenY - (viewportTop + thresholdY);
        } else if (screenY > viewportBottom - thresholdY) {
            panY = screenY - (viewportBottom - thresholdY);
        }

        if (Math.abs(panX) < 0.001 && Math.abs(panY) < 0.001) {
            return false;
        }

        return camera.panByViewportPixels(
            THREE.MathUtils.clamp(panX, -maxPanPixels, maxPanPixels),
            THREE.MathUtils.clamp(panY, -maxPanPixels, maxPanPixels)
        );
    }

    /**
     * Calculates 
     * @param {number} x 
     * @param {number} y 
     * @param {boolean} initial 
     */
    setDrag(x, y, initial = false) {
        const draggingFromCache = this.dragArrayModel !== null && !this.isDraggable;
        if (initial) {
            const first = this.overObject(x, y);
            if (!draggingFromCache && first && (this.#targetOnDown === null || first.object instanceof PlanogramObject && this.#targetDraggable instanceof PlanogramObject && first.object.uid !== this.#targetDraggable.uid)) return;
        }
        if (this.isDraggable || draggingFromCache) {
            const translatedCursor = this.#getTranslatedCursor(x, y, true);
            this.#raycaster.setFromCamera(translatedCursor, this.focusedCamera.camera);
            const group = draggingFromCache ? this.dragArrayModel : this.#targetDraggable.group;
            if (initial) this.#plane.setFromNormalAndCoplanarPoint(this.focusedCamera.camera.getWorldDirection(this.#plane.normal),
                this.#worldPosition.setFromMatrixPosition(group.matrixWorld));
            if (this.#raycaster.ray.intersectPlane(this.#plane, this.#intersection)) {
                if (initial) {
                    this.#inverseMatrix.copy(this.group.matrixWorld).invert();
                    this.#offset.copy(this.#intersection).sub(this.#worldPosition.setFromMatrixPosition(group.matrixWorld));
                    if (draggingFromCache) {
                        this.#offset.set(0, 0, 0);
                        if (this.#updateDragVectorFromCursor(x, y)) {
                            this.#dragStartVector.copy(this.#dragVector);
                            this.setDragArrayPosition(new THREE.Vector2(x, y));
                        }
                    } else if (this.#targetDraggable instanceof PlanogramBlockingDivider) {
                        const planBox = this.planogramBox.clone();
                        const planSize = planBox.getSize(new THREE.Vector3());
                        const planMin = planBox.min.clone();
                        const divider = this.#targetDraggable;
                        const dividerCenterLocal = planMin.clone().add(divider.getPosition(planSize));
                        const pointerLocal = this.#intersection.clone().sub(this.#offset).applyMatrix4(this.#inverseMatrix);
                        this.#dividerDragOffset = pointerLocal.clone().sub(dividerCenterLocal);
                        this.#createDividerOutline(divider);
                    } else {
                        this.#dividerDragOffset = null;
                        if (this.#updateDragVectorFromCursor(x, y)) {
                            this.#dragStartVector.copy(this.#dragVector);
                        }
                    }
                } else {
                    if (!this.#updateDragVectorFromCursor(x, y)) {
                        return;
                    }

                    if (!draggingFromCache && this.#targetDraggable instanceof PlanogramBlockingDivider) {
                        this.setDividerPosition(new THREE.Vector2(x, y));
                    } else {
                        const cursorPosition = new THREE.Vector2(x, y);
                        this.setDragArrayPosition(cursorPosition);

                        // Components are placed within the fixture in view, so don't auto-pan
                        // the camera towards the viewport edges while dragging one.
                        const autoPan = !(this.dragArrayModel?.target instanceof PlanogramComponent);
                        if (autoPan && this.#panCameraForDraggedTarget(this.dragArrayModel?.position) && this.#updateDragVectorFromCursor(x, y)) {
                            this.setDragArrayPosition(cursorPosition);
                        }
                    }
                }
            }
        }
    }

    onMouseDown(x, y, leftClick = true) {
        if (this.addBox) return;
        this.#downPosition = new THREE.Vector2(x, y);
        const result = this.overObject(x, y, true);
        this.#targetOnDown = result?.object;
        if (leftClick && this.#targetOnDown) {
            const planogramBlocking = this.querySelector("planogram-blocking");
            const isAddDivider = planogramBlocking && (planogramBlocking.addVerticalDivider || planogramBlocking.addHorizontalDivider);
            const isBlocking = result.intersection.object.name === PlanogramBlocking.name && isAddDivider;
            if (this.#targetOnDown instanceof PlanogramBlockingDivider && !isBlocking) {
            } else if (isBlocking) {
                planogramBlocking.raiseClickEvent(result.intersection);
            }
        }
    }

    onMouseUp(x, y, raiseClick = true) {
        if (this.addBox) return;
        const intersection = this.#getFirstIntersection(x, y);
        const withinThreshold = PlanogramContainer.isWithinThreshold(new THREE.Vector2(x, y), this.#downPosition);
        if (intersection) {
            const planogramBlocking = this.querySelector("planogram-blocking");
            const isAddDivider = planogramBlocking && (planogramBlocking.addVerticalDivider || planogramBlocking.addHorizontalDivider);
            const isBlocking = intersection.object.name === PlanogramBlocking.name && isAddDivider;
            if (withinThreshold) {
                if (raiseClick) {
                    if (this.#targetOnDown && intersection) {
                        if (this.#targetOnDown.uid === intersection.object.userData.id) {
                            this.#targetOnDown.raiseClickEvent(x, y, true);
                        } else {
                            const blocking = planogramBlocking.getIntersectedBox(intersection);
                            if (blocking instanceof PlanogramBlockingPosition) {
                                blocking.raiseClickEvent(x, y, true);
                            } else if (blocking instanceof PlanogramBlockingDivider) {
                                blocking.raiseClickEvent(x, y, true);
                            }
                        }
                    }
                } else {
                    if (!isBlocking) {
                        if (this.#targetOnDown instanceof PlanogramBlockingDivider) {
                            this.#targetOnDown.raiseClickEvent(x, y, false);
                        } else {
                            this.#targetOnDown.raiseClickEvent(x, y, raiseClick);
                        }
                    }
                }
            }
        } else if (withinThreshold) {
            this.raiseClickEvent(x, y, raiseClick);
        }
        if (this.#dividerOutline) {
            this.#removeDividerOutline(true);
        }
        this.#targetOnDown = null;
        this.#downPosition = null;
    }

    /**
     * @param {number} x
     * @param {number} y
     * @returns {{ cursor: string, isDraggable:boolean, object: PlanogramObject|PlanogramBlockingDivider|PlanogramBlockingPosition|PlanogramSubcomponent, intersection: THREE.Intersection }|null}
     */
    overObject(x, y, raiseClick = false) {
        if (this.addBox) return null;
        const blocking = this.querySelector("planogram-blocking");
        const intersection = this.#getFirstIntersection(x, y);
        if (!intersection) {
            blocking?.updateShaderAttributes(-1);
            return null;
        }
        const planObject = this.getPlanogramObjectFromUid(intersection.object.userData.id);
        if (!planObject) return null;
        const options = { cursor: 'default', isDraggable: true, object: planObject, intersection: intersection };
        if (intersection.object.name === PlanogramBlocking.name || intersection.object.name === PlanogramComponent.name) {
            if (planObject instanceof PlanogramBlocking) {
                const item = planObject.getIntersectedBox(intersection) ?? planObject;
                if (item instanceof PlanogramBlockingDivider && (!blocking.addVerticalDivider && !blocking.addHorizontalDivider)) {
                    blocking?.updateShaderAttributes(-1);
                    return { ...options, isDraggable: false, cursor: "grab", object: item };
                } else if (item instanceof PlanogramBlockingPosition) {
                    planObject.updateShaderAttributes(intersection.instanceId, intersection.uv, intersection.normal);
                    return { ...options, isDraggable: false, cursor: "default", object: item };
                } else {
                    if (blocking.addVerticalDivider || blocking.addHorizontalDivider) {
                        blocking.updateShaderAttributes(intersection.instanceId, intersection.uv, intersection.normal);
                    } else {
                        blocking.updateShaderAttributes(-1);
                    }
                }
            }
            if (planObject instanceof PlanogramComponent && planObject.editCustom) {
                const subcomponent = planObject.getTargetedSubComponent(intersection);
                return (subcomponent?.selected ?? false) ? { ...options, object: subcomponent } : null;
            }
        }
        return (raiseClick || planObject.selected) ? options : null;
    }

    //#endregion

    //#region DragArray Methods

    /**
     * Constructs a drag array from the selected objects
     */
    constructDragArray() {
        if (!this.dragArrayModel && this.isDraggable && !(this.#targetOnDown instanceof PlanogramBlockingDivider)) {
            const items = [...this.querySelectorAll("[selected]")];
            this.dragArrayModel = new DragArray(this.#targetDraggable, items );
            this.focusedCamera.camera.layers.enable(PlanogramRendererResources.DRAG_LAYER);
            this.group.add(this.dragArrayModel);
        }
    }

    /**
     * Constructs a drag array from drag cache
     */
    constructDragArrayFromDragCache() {
        if(!this.dragArrayModel && DragCache.activeCache){
            const payload = DragCache.activeCache.gatherAll();
            const cachedItems = payload?.cachedItems ?? [];
            const totalWidth = cachedItems.reduce((sum, item) => sum + (Number(item.width) || 0), 0);
            let offsetX = totalWidth / -2;
            const boxes = cachedItems.map(item => {
                const size = new THREE.Vector3(
                    Number(item.width) || 0,
                    Number(item.height) || 0,
                    Number(item.depth) || 0
                );
                const centre = new THREE.Vector3(offsetX + (size.x / 2), 0, 0);
                offsetX += size.x;
                return new THREE.Box3().setFromCenterAndSize(centre, size);
            });
            this.dragArrayModel = new DragArray( null, null, boxes, DragCache.activeType );
            this.focusedCamera.camera.layers.enable(PlanogramRendererResources.DRAG_LAYER);
            this.group.add(this.dragArrayModel);
        }
    }

    /**
     * Drops the drag array and can raise the drop event
     * @param {boolean} raiseEvent 
     */
    clearDragArray(raiseEvent = false) {
        if (this.dragArrayModel) {
            this.clearDecorators();
            this.dragArrayModel.dropDragArray(raiseEvent);
            this.dragArrayModel = null;
            this.focusedCamera.camera.layers.disable(PlanogramRendererResources.DRAG_LAYER);
        }
        this.removeSnapDecorator();
        // remove any divider outline preview if present
        this.#removeDividerOutline(raiseEvent);
    }


    //#endregion

    //#region Rotation Gizmo Methods

    /** @returns {PlanogramObject[]} currently selected rotatable objects */
    #selectedRotatables() {
        return [...this.querySelectorAll("[selected]")].filter(x => x instanceof PlanogramObject);
    }

    /** @returns {THREE.Box3|null} combined world bounding box of the selection */
    #selectionBox() {
        const selected = this.#selectedRotatables();
        if (selected.length === 0) return null;
        return selected.reduce((box, obj) => box.union(this.#objectWorldBox(obj)), new THREE.Box3());
    }

    /**
     * Container-space AABB of an object including its rotation. groupBoundingBox is the
     * object's un-rotated local box, so we transform it by the group's actual matrix (the
     * same transform setMatrix applied) to get a box that truly encloses the rotated object.
     * @param {PlanogramObject} obj
     * @returns {THREE.Box3}
     */
    #objectWorldBox(obj) {
        const mb = obj.mainBlockBoundingBox;
        const local = (mb && !mb.isEmpty()) ? mb : obj.groupBoundingBox;
        if (obj.group && !local.isEmpty()) {
            obj.group.updateMatrix();
            return local.clone().applyMatrix4(obj.group.matrix);
        }
        return local.clone().translate(new THREE.Vector3(obj.x, obj.y, obj.z));
    }

    /**
     * Builds a DragArray-style preview (one ghost box per selected object) that mirrors each
     * object's current orientation, positioned relative to the given pivot. The unit cube is
     * mapped onto each object's local box, then transformed by the object's actual matrix.
     * @param {THREE.Vector3} pivot
     * @returns {THREE.InstancedMesh}
     */
    #buildTransformPreview(pivot) {
        const selected = this.#selectedRotatables();
        const preview = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), PlanogramRendererResources.DragShader, selected.length);
        const sizes = [];
        const bases = [];
        selected.forEach((obj, i) => {
            const mb = obj.mainBlockBoundingBox;
            const local = (mb && !mb.isEmpty()) ? mb : obj.groupBoundingBox;
            const localCentre = local.getCenter(new THREE.Vector3());
            const localSize = local.getSize(new THREE.Vector3());
            obj.group?.updateMatrix();
            const objectMatrix = obj.group ? obj.group.matrix : new THREE.Matrix4().makeTranslation(obj.x, obj.y, obj.z);
            const matrix = new THREE.Matrix4().makeTranslation(-pivot.x, -pivot.y, -pivot.z)
                .multiply(objectMatrix)
                .multiply(new THREE.Matrix4().makeTranslation(localCentre.x, localCentre.y, localCentre.z))
                .multiply(new THREE.Matrix4().makeScale(localSize.x || 1e-3, localSize.y || 1e-3, localSize.z || 1e-3));
            preview.setMatrixAt(i, matrix);
            sizes.push(localSize.x, localSize.y, localSize.z);
            const centre = localCentre.clone().applyMatrix4(objectMatrix).sub(pivot);
            const quaternion = obj.group ? obj.group.quaternion.clone() : new THREE.Quaternion();
            bases.push({ centre, quaternion, size: localSize.clone() });
        });
        preview.geometry.setAttribute("size", new THREE.InstancedBufferAttribute(new Float32Array(sizes), 3));
        preview.layers.set(PlanogramRendererResources.DRAG_LAYER);
        preview.renderOrder = 9999;
        preview.position.copy(pivot);
        preview.userData.transformBases = bases;
        return preview;
    }

    /**
     * Re-derives every resize-preview instance matrix as T(centre) * R * S(size), scaling each
     * box in its own local frame about the fixed anchor. Scaling the mesh as a whole would put
     * the world-axis scale outside each instance's rotation and shear rotated boxes; this keeps
     * every box a box and matches what endResize commits (size *= s per axis, rotation kept).
     * @param {THREE.Vector3} scale per-axis scale factors
     */
    #applyResizeScale(scale) {
        const preview = this.#resizePreview;
        const bases = preview?.userData.transformBases;
        if (!bases) return;
        const anchorLocal = this.#resizeAnchor.clone().sub(this.#resizeCentre);
        const sizeAttr = preview.geometry.getAttribute("size");
        const translation = new THREE.Matrix4();
        const rotation = new THREE.Matrix4();
        const scaling = new THREE.Matrix4();
        const matrix = new THREE.Matrix4();
        bases.forEach((base, i) => {
            // Centre scales about the fixed anchor along world axes (matches endResize).
            const cx = anchorLocal.x + (base.centre.x - anchorLocal.x) * scale.x;
            const cy = anchorLocal.y + (base.centre.y - anchorLocal.y) * scale.y;
            const cz = anchorLocal.z + (base.centre.z - anchorLocal.z) * scale.z;
            // A negative scale flips the centre to the other side of the anchor (above); the
            // size, however, must stay positive, so take the magnitude of the scaled extent.
            const sx = Math.max(Math.abs(base.size.x * scale.x), 1e-3);
            const sy = Math.max(Math.abs(base.size.y * scale.y), 1e-3);
            const sz = Math.max(Math.abs(base.size.z * scale.z), 1e-3);
            translation.makeTranslation(cx, cy, cz);
            rotation.makeRotationFromQuaternion(base.quaternion);
            scaling.makeScale(sx, sy, sz);
            matrix.multiplyMatrices(translation, rotation).multiply(scaling);
            preview.setMatrixAt(i, matrix);
            sizeAttr?.setXYZ(i, sx, sy, sz);
        });
        preview.instanceMatrix.needsUpdate = true;
        if (sizeAttr) sizeAttr.needsUpdate = true;
        preview.position.copy(this.#resizeCentre);
        preview.scale.set(1, 1, 1);
    }

    /** Rebuilds the rotation gizmo for the current selection (deferred so geometry is ready). */
    refreshRotationGizmo() {
        queueMicrotask(() => {
            if (this.#rotating || this.#resizing) return;
            this.#clearRotationGizmo();
            if (!this.componentTransform) return;
            const box = this.#selectionBox();
            if (!box || !this.focusedCamera) return;
            this.#rotationGizmo = new PlanogramComponentTransformController(box, this.#orthographicViewAxis());
            this.group.add(this.#rotationGizmo);
            this.focusedCamera.camera.layers.enable(PlanogramRendererResources.GIZMO_LAYER);
        });
    }

    /** @returns {"x"|"y"|"z"|null} */
    #orthographicViewAxis() {
        const camera = this.focusedCamera?.camera;
        if (!(camera instanceof THREE.OrthographicCamera)) return null;

        const forward = camera.getWorldDirection(new THREE.Vector3());
        const absForward = new THREE.Vector3(Math.abs(forward.x), Math.abs(forward.y), Math.abs(forward.z));
        if (absForward.x >= absForward.y && absForward.x >= absForward.z) return "x";
        if (absForward.y >= absForward.x && absForward.y >= absForward.z) return "y";
        return "z";
    }

    #clearRotationGizmo() {
        if (this.#rotationGizmo) {
            this.#rotationGizmo.dispose();
            this.#rotationGizmo = null;
        }
        this.focusedCamera?.camera.layers.disable(PlanogramRendererResources.GIZMO_LAYER);
    }

    /**
     * Picks a gizmo handle under the cursor: a rotation ring or a corner resize handle.
     * @param {number} x
     * @param {number} y
     * @returns {{ axis:"x"|"y"|"z" }|{ resize:true, corner:THREE.Vector3 }|null}
     */
    gizmoIntersection(x, y) {
        if (!this.#rotationGizmo || !this.focusedCamera) return null;
        const raycaster = new THREE.Raycaster();
        raycaster.layers.set(PlanogramRendererResources.GIZMO_LAYER);
        raycaster.setFromCamera(this.#getTranslatedCursor(x, y), this.focusedCamera.camera);
        // The wireframe box is not interactive, and hidden rotation rings shouldn't be pickable either.
        const targets = this.#rotationGizmo.children.filter(c => c.userData.role !== "box" && c.visible);
        const [hit] = raycaster.intersectObjects(targets, false);
        if (!hit) return null;
        if (hit.object.userData.role === "resize")
            return { resize: true, corner: hit.object.userData.corner.clone() };
        return { axis: hit.object.userData.axis };
    }

    /**
     * Projects the cursor onto the active rotation plane.
     * @param {number} x
     * @param {number} y
     * @returns {THREE.Vector3|null}
     */
    #cursorOnRotationPlane(x, y) {
        this.#raycaster.setFromCamera(this.#getTranslatedCursor(x, y), this.focusedCamera.camera);
        return this.#raycaster.ray.intersectPlane(this.#rotatePlane, this.#rotatePoint);
    }

    #angleOnRing(point) {
        const d = point.clone().sub(this.#rotateCentre);
        return Math.atan2(d.dot(this.#rotateV), d.dot(this.#rotateU));
    }

    /**
     * Begins a rotation drag on the given gizmo ring.
     * @param {"x"|"y"|"z"} axis
     * @param {number} x
     * @param {number} y
     */
    beginRotation(axis, x, y) {
        const box = this.#selectionBox();
        if (!box || !this.#rotationGizmo) return;
        this.#rotateAxis = axis;
        this.#rotateNormal.copy(this.#rotationGizmo.axisVector(axis)).normalize();
        box.getCenter(this.#rotateCentre);

        // Orthonormal basis of the ring plane.
        const ref = Math.abs(this.#rotateNormal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
        this.#rotateU.copy(ref).cross(this.#rotateNormal).normalize();
        this.#rotateV.copy(this.#rotateNormal).cross(this.#rotateU).normalize();
        this.#rotatePlane.setFromNormalAndCoplanarPoint(this.#rotateNormal, this.#rotateCentre);

        const start = this.#cursorOnRotationPlane(x, y);
        if (!start) return;
        this.#rotatePrevAngle = this.#angleOnRing(start);
        this.#rotateDelta = 0;
        this.#rotateAppliedDelta = 0;

        // Outline preview that mirrors each object's current orientation.
        const preview = this.#buildTransformPreview(this.#rotateCentre);
        this.group.add(preview);
        this.#rotatePreview = preview;
        this.focusedCamera.camera.layers.enable(PlanogramRendererResources.DRAG_LAYER);
        if (this.#rotationGizmo) this.#rotationGizmo.visible = false;
        this.#rotating = true;
    }

    /**
     * Updates the rotation preview to follow the cursor.
     * @param {number} x
     * @param {number} y
     * @param {boolean} shiftKey
     */
    updateRotation(x, y, shiftKey = false, ctrlKey = false) {
        if (!this.#rotating) return;
        const point = this.#cursorOnRotationPlane(x, y);
        if (!point) return;
        const angle = this.#angleOnRing(point);
        let d = angle - this.#rotatePrevAngle;
        while (d > Math.PI) d -= 2 * Math.PI;
        while (d < -Math.PI) d += 2 * Math.PI;
        this.#rotateDelta += d;
        this.#rotatePrevAngle = angle;

        const step = Math.PI / 12; // 15 degrees
        this.#rotateAppliedDelta = ctrlKey ? Math.round(this.#rotateDelta / step) * step : this.#rotateDelta;
        this.#rotatePreview?.setRotationFromAxisAngle(this.#rotateNormal, this.#rotateAppliedDelta);
    }

    /**
     * Ends the rotation drag, optionally committing it by raising rotate events.
     * @param {boolean} commit
     */
    endRotation(commit = true) {
        if (!this.#rotating) return;
        const delta = this.#rotateAppliedDelta;
        const normal = this.#rotateNormal.clone();
        const centre = this.#rotateCentre.clone();
        const selected = this.#selectedRotatables();

        if (this.#rotatePreview) {
            this.#rotatePreview.geometry.dispose();
            this.#rotatePreview.removeFromParent();
            this.#rotatePreview = null;
        }
        this.focusedCamera?.camera.layers.disable(PlanogramRendererResources.DRAG_LAYER);
        this.#rotating = false;
        this.#rotateAxis = null;
        if (this.#rotationGizmo) this.#rotationGizmo.visible = true;

        if (commit && Math.abs(delta) > 1e-6) {
            const qDelta = new THREE.Quaternion().setFromAxisAngle(normal, delta);
            for (const obj of selected) {

                const oldCentre = this.#objectWorldBox(obj).getCenter(new THREE.Vector3());
                const newCentre = oldCentre.clone().sub(centre).applyQuaternion(qDelta).add(centre);
                const posDelta = newCentre.sub(oldCentre);
                const px = obj.x + posDelta.x, py = obj.y + posDelta.y, pz = obj.z + posDelta.z;

                const oldVisual = new THREE.Quaternion().setFromEuler(new THREE.Euler(-obj.rotationX, -obj.rotationY, -obj.rotationZ, 'XYZ'));
                const newVisual = new THREE.Euler().setFromQuaternion(qDelta.clone().multiply(oldVisual), 'XYZ');

                const sizeBox = obj.mainBlockBoundingBox;
                const size = (sizeBox && !sizeBox.isEmpty() ? sizeBox : obj.groupBoundingBox).getSize(new THREE.Vector3());
                obj.raiseTransformEvent(px, py, pz, -newVisual.x, -newVisual.y, -newVisual.z, size.x, size.y, size.z);
            }
        }
        this.refreshRotationGizmo();
    }

    /**
     * Projects the cursor onto the active resize plane (a plane through the selection
     * centre facing the camera).
     * @param {number} x
     * @param {number} y
     * @returns {THREE.Vector3|null}
     */
    #cursorOnResizePlane(x, y) {
        this.#raycaster.setFromCamera(this.#getTranslatedCursor(x, y), this.focusedCamera.camera);
        return this.#raycaster.ray.intersectPlane(this.#resizePlane, this.#resizePoint);
    }

    /**
     * Begins a uniform resize drag on the given corner handle.
     * @param {THREE.Vector3} corner sign vector (±1,±1,±1) of the grabbed corner
     * @param {number} x
     * @param {number} y
     */
    beginResize(corner, x, y) {
        const box = this.#selectionBox();
        if (!box || !this.#rotationGizmo || !this.focusedCamera) return;
        box.getCenter(this.#resizeCentre);
        const half = box.getSize(new THREE.Vector3()).multiplyScalar(0.5);
        this.#resizeCorner.copy(corner);
        const offset = new THREE.Vector3(corner.x * half.x, corner.y * half.y, corner.z * half.z);
        this.#resizeAnchor.copy(this.#resizeCentre).sub(offset);
        this.#resizeGrab.copy(this.#resizeCentre).add(offset);
        this.#resizeOldExtent.copy(this.#resizeGrab).sub(this.#resizeAnchor);
        this.#resizeScaleVec.set(1, 1, 1);
        const viewDir = new THREE.Vector3();
        this.focusedCamera.camera.getWorldDirection(viewDir);
        const ax = Math.abs(viewDir.x), ay = Math.abs(viewDir.y), az = Math.abs(viewDir.z);
        const normal = ax >= ay && ax >= az ? new THREE.Vector3(1, 0, 0)
            : ay >= az ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
        this.#resizePlane.setFromNormalAndCoplanarPoint(normal, this.#resizeGrab);

        const preview = this.#buildTransformPreview(this.#resizeCentre);
        this.group.add(preview);
        this.#resizePreview = preview;
        this.focusedCamera.camera.layers.enable(PlanogramRendererResources.DRAG_LAYER);
        if (this.#rotationGizmo) this.#rotationGizmo.visible = false;
        this.#resizing = true;
    }

    /**
     * Updates the resize preview to follow the cursor (uniform scale about the centre).
     * @param {number} x
     * @param {number} y
     * @param {boolean} shiftKey snap the scale to 5% increments
     */
    updateResize(x, y, shiftKey = false, ctrlKey = false) {
        if (!this.#resizing) return;
        const point = this.#cursorOnResizePlane(x, y);
        if (!point) return;
        // Ctrl snaps the dragged corner to the nearest grid point.
        if (ctrlKey) this.#snapToGrid(point);
        const newExtent = point.clone().sub(this.#resizeAnchor);
        const e = this.#resizeOldExtent;
        const s = this.#resizeScaleVec;
        s.set(
            Math.abs(e.x) > 1e-4 ? newExtent.x / e.x : 1,
            Math.abs(e.y) > 1e-4 ? newExtent.y / e.y : 1,
            Math.abs(e.z) > 1e-4 ? newExtent.z / e.z : 1
        );
        if (shiftKey) {
            const dx = Math.abs(s.x - 1), dy = Math.abs(s.y - 1), dz = Math.abs(s.z - 1);
            if (dx >= dy && dx >= dz) s.set(s.x, 1, 1);
            else if (dy >= dz) s.set(1, s.y, 1);
            else s.set(1, 1, s.z);
        }
        const clampMagnitude = v => (v < 0 ? -1 : 1) * Math.max(Math.abs(v), 0.01);
        s.set(clampMagnitude(s.x), clampMagnitude(s.y), clampMagnitude(s.z));
        this.#applyResizeScale(s);
    }

    /**
     * Ends the resize drag, optionally committing it by raising transform events.
     * @param {boolean} commit
     */
    endResize(commit = true) {
        if (!this.#resizing) return;
        const s = this.#resizeScaleVec.clone();
        const anchor = this.#resizeAnchor.clone();
        const selected = this.#selectedRotatables();

        if (this.#resizePreview) {
            this.#resizePreview.geometry.dispose();
            this.#resizePreview.removeFromParent();
            this.#resizePreview = null;
        }
        this.focusedCamera?.camera.layers.disable(PlanogramRendererResources.DRAG_LAYER);
        this.#resizing = false;
        if (this.#rotationGizmo) this.#rotationGizmo.visible = true;

        const changed = Math.abs(s.x - 1) > 1e-4 || Math.abs(s.y - 1) > 1e-4 || Math.abs(s.z - 1) > 1e-4;
        if (commit && changed) {
            for (const obj of selected) {
                const mainBox = obj.mainBlockBoundingBox;
                const oldSize = (mainBox && !mainBox.isEmpty() ? mainBox : obj.groupBoundingBox).getSize(new THREE.Vector3());
                const oldCentre = this.#objectWorldBox(obj).getCenter(new THREE.Vector3());
                // Scale each object's size and centre per axis about the fixed anchor corner.
                const newCentre = new THREE.Vector3(
                    anchor.x + (oldCentre.x - anchor.x) * s.x,
                    anchor.y + (oldCentre.y - anchor.y) * s.y,
                    anchor.z + (oldCentre.z - anchor.z) * s.z);
                const posDelta = newCentre.clone().sub(oldCentre);
                const px = obj.x + posDelta.x, py = obj.y + posDelta.y, pz = obj.z + posDelta.z;
                // newCentre already mirrors past the anchor when a scale is negative; the
                // committed size must stay positive, so use the scale magnitude here.
                const w = oldSize.x * Math.abs(s.x), h = oldSize.y * Math.abs(s.y), d = oldSize.z * Math.abs(s.z);
                // Rotation is unchanged by a resize, so forward the current attribute values.
                obj.raiseTransformEvent(px, py, pz, obj.rotationX, obj.rotationY, obj.rotationZ, w, h, d);
            }
        }
        this.refreshRotationGizmo();
    }

    //#endregion

    //#region Pointer Event Handlers for Divider Dragging

    //#region Add-box drawing

    /** World-space Y of the planogram floor (falls back to 0 for an empty planogram). */
    #computeFloorWorldY() {
        const y = this.planogramBox?.min?.y;
        return Number.isFinite(y) ? y : 0;
    }

    /**
     * Raycast the cursor onto the horizontal floor plane and write the planogram-local hit into out.
     * @param {number} x
     * @param {number} y
     * @param {THREE.Vector3} out
     * @returns {boolean} true if the ray hit the floor plane
     */
    #raycastFloorLocal(x, y, out) {
        if (!this.focusedCamera) return false;
        const translatedCursor = this.#getTranslatedCursor(x, y);
        this.#raycaster.setFromCamera(translatedCursor, this.focusedCamera.camera);
        const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -this.#boxFloorWorldY);
        const hit = new THREE.Vector3();
        if (!this.#raycaster.ray.intersectPlane(plane, hit)) return false;
        this.#inverseMatrix.copy(this.group.matrixWorld).invert();
        out.copy(hit).applyMatrix4(this.#inverseMatrix);
        return true;
    }

    /**
     * Raycast the cursor onto a vertical plane through the footprint's far corner (facing the
     * camera horizontally) and return the box height above the floor.
     * @param {number} x
     * @param {number} y
     * @returns {number} local height (>= 0)
     */
    #raycastHeightLocal(x, y) {
        if (!this.focusedCamera) return this.#boxHeight;
        const translatedCursor = this.#getTranslatedCursor(x, y);
        this.#raycaster.setFromCamera(translatedCursor, this.focusedCamera.camera);
        const normal = this.focusedCamera.camera.getWorldDirection(new THREE.Vector3());
        normal.y = 0;
        if (normal.lengthSq() < 1e-6) normal.set(0, 0, 1);
        normal.normalize();
        const worldEnd = this.#boxEndLocal.clone().applyMatrix4(this.group.matrixWorld);
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, worldEnd);
        const hit = new THREE.Vector3();
        if (!this.#raycaster.ray.intersectPlane(plane, hit)) return this.#boxHeight;
        return Math.max(0, hit.y - this.#boxFloorWorldY);
    }

    /**
     * Raycast the cursor onto the vertical plane x=anchorWorld or z=anchorWorld and write the
     * planogram-local hit into out. Used for the Front/Back and Left/Right Orthographic views,
     * where the camera looks straight down x or z, so the drawable plane isn't the floor.
     * @param {number} x
     * @param {number} y
     * @param {"x"|"z"} axis
     * @param {number} anchorWorld
     * @param {THREE.Vector3} out
     * @returns {boolean}
     */
    #raycastAxisPlaneLocal(x, y, axis, anchorWorld, out) {
        if (!this.focusedCamera) return false;
        const translatedCursor = this.#getTranslatedCursor(x, y);
        this.#raycaster.setFromCamera(translatedCursor, this.focusedCamera.camera);
        const normal = axis === "x" ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1);
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, normal.clone().multiplyScalar(anchorWorld));
        const hit = new THREE.Vector3();
        if (!this.#raycaster.ray.intersectPlane(plane, hit)) return false;
        this.#inverseMatrix.copy(this.group.matrixWorld).invert();
        out.copy(hit).applyMatrix4(this.#inverseMatrix);
        return true;
    }

    /**
     * The sign the hidden dimension should extend in the Front/Back/Left/Right Orthographic
     * views: away from the camera, so the drawn face is the visible one and the box grows
     * back into the scene rather than towards the viewer.
     * @param {"x"|"z"} axis
     * @returns {number}
     */
    #orthoBoxDirection(axis) {
        const camera = this.focusedCamera?.camera;
        if (!camera) return 1;
        const forward = camera.getWorldDirection(new THREE.Vector3());
        return Math.sign(forward[axis]) || 1;
    }

    /**
     * Resolves the starting point/plane for an add-box gesture at the given screen position,
     * covering both the floor plane (Perspective and the Top/Bottom Orthographic views) and
     * the two vertical planes (Front/Back, Left/Right Orthographic views).
     * @param {number} x
     * @param {number} y
     * @returns {{axis:"x"|"y"|"z"|null, direction:number, anchorWorld:number, start:THREE.Vector3}|null}
     */
    #resolveAddBoxStart(x, y) {
        const axis = this.#orthographicViewAxis();
        const start = new THREE.Vector3();
        const hit = this.#getFirstIntersection(x, y);

        if (axis === "x" || axis === "z") {
            const direction = this.#orthoBoxDirection(axis);
            let anchorWorld;
            if (hit?.point) {
                this.#inverseMatrix.copy(this.group.matrixWorld).invert();
                start.copy(hit.point).applyMatrix4(this.#inverseMatrix);
                anchorWorld = hit.point[axis];
            } else {
                anchorWorld = 0;
                if (!this.#raycastAxisPlaneLocal(x, y, axis, anchorWorld, start)) return null;
            }
            if (PlanogramRenderer.get(this)?.ctrlKey) {
                this.#snapToGrid(start);
                anchorWorld = start.clone().applyMatrix4(this.group.matrixWorld)[axis];
            }
            return { axis, direction, anchorWorld, start };
        }

        let floorWorldY;
        if (hit?.point) {
            this.#inverseMatrix.copy(this.group.matrixWorld).invert();
            start.copy(hit.point).applyMatrix4(this.#inverseMatrix);
            floorWorldY = hit.point.y;
        } else {
            floorWorldY = this.#computeFloorWorldY();
            if (!this.#raycastFloorLocal(x, y, start)) return null;
        }
        if (PlanogramRenderer.get(this)?.ctrlKey) {
            this.#snapToGrid(start);
            floorWorldY = start.clone().applyMatrix4(this.group.matrixWorld).y;
        }
        return { axis, direction: 1, anchorWorld: floorWorldY, start };
    }

    /**
     * Double-click shortcut: drop a default-sized box centred on the clicked point, without
     * going through the drag gesture. The hidden dimension (Height when looking down, Depth or
     * Width in the vertical Orthographic views) extends from the clicked surface like a drawn one would.
     * @param {number} x
     * @param {number} y
     * @returns {boolean} true if a box was added
     */
    addDefaultBox(x, y) {
        if (!this.addBox || this.#addBoxStage !== 0) return false;
        const resolved = this.#resolveAddBoxStart(x, y);
        if (!resolved) return false;

        const { axis, direction, start } = resolved;
        const hiddenAxis = axis ?? "y";
        const half = PlanogramContainer.#DEFAULT_BOX_SIZE / 2;
        const min = new THREE.Vector3(start.x - half, start.y - half, start.z - half);
        const max = new THREE.Vector3(start.x + half, start.y + half, start.z + half);
        const hiddenNear = start[hiddenAxis];
        const hiddenFar = hiddenNear + direction * PlanogramContainer.#DEFAULT_BOX_SIZE;
        min[hiddenAxis] = Math.min(hiddenNear, hiddenFar);
        max[hiddenAxis] = Math.max(hiddenNear, hiddenFar);

        this.raiseNewBoxEvent(min, max);
        return true;
    }

    /** Create the wireframe preview box (a unit box scaled per update) on the drag layer. */
    #ensureBoxPreview() {
        if (this.#boxPreview) return;
        // Dashed outline; geometry is rebuilt at real size each update so the dashes stay uniform.
        const material = new THREE.LineDashedMaterial({ color: 0x000000, depthTest: false, transparent: true, dashSize: 1.5, gapSize: 0.75 });
        this.#boxPreview = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1)), material);
        this.#boxPreview.layers.set(PlanogramRendererResources.DRAG_LAYER);
        this.#boxPreview.renderOrder = 9999;
        this.focusedCamera?.camera.layers.enable(PlanogramRendererResources.DRAG_LAYER);
        this.group.add(this.#boxPreview);
    }

    /**
     * Rebuild and reposition the preview box from the current footprint and hidden dimension.
     * In an Orthographic view the hidden dimension (whichever one the camera can't draw) is
     * always shown at its default size, since that view auto-finalises without a height stage.
     */
    #updateBoxPreview() {
        if (!this.#boxPreview) return;
        const start = this.#boxStartLocal, end = this.#boxEndLocal;
        const axis = this.#addBoxOrthoAxis;
        const hiddenAxis = axis ?? "y";
        const direction = hiddenAxis === "y" ? 1 : this.#addBoxOrthoDirection;
        const hasHeight = axis !== null || (this.#addBoxStage === 2 && this.#boxHeight > 0.001);
        const hiddenSize = axis !== null ? PlanogramContainer.#DEFAULT_BOX_SIZE : (hasHeight ? this.#boxHeight : 0);

        const width = hiddenAxis === "x" ? Math.max(hiddenSize, 0.001) : Math.max(Math.abs(end.x - start.x), 0.001);
        const height = hiddenAxis === "y" ? Math.max(hiddenSize, 0.001) : Math.max(Math.abs(end.y - start.y), 0.001);
        const depth = hiddenAxis === "z" ? Math.max(hiddenSize, 0.001) : Math.max(Math.abs(end.z - start.z), 0.001);

        this.#boxPreview.geometry.dispose();
        const geometry = hasHeight
            ? new THREE.EdgesGeometry(new THREE.BoxGeometry(width, height, depth))
            : PlanogramContainer.#buildRectangleEdges(width, depth);
        PlanogramContainer.#resetSegmentLineDistances(geometry);
        this.#boxPreview.geometry = geometry;
        this.#boxPreview.position.set(
            (start.x + end.x) / 2 + (hiddenAxis === "x" ? direction * hiddenSize / 2 : 0),
            (start.y + end.y) / 2 + (hiddenAxis === "y" ? direction * hiddenSize / 2 : 0),
            (start.z + end.z) / 2 + (hiddenAxis === "z" ? direction * hiddenSize / 2 : 0)
        );
        const dash = Math.max(Math.max(width, height, depth) / 20, 0.5);
        this.#boxPreview.material.dashSize = dash;
        this.#boxPreview.material.gapSize = dash;
    }

    /**
     * Build a flat rectangle outline (4 edges in the local XZ plane, centred on the origin).
     * @param {number} width
     * @param {number} depth
     * @returns {THREE.BufferGeometry}
     */
    static #buildRectangleEdges(width, depth) {
        const hw = width / 2, hd = depth / 2;
        const positions = new Float32Array([
            -hw, 0, -hd, hw, 0, -hd, // back
            hw, 0, -hd, hw, 0, hd, // right
            hw, 0, hd, -hw, 0, hd, // front
            -hw, 0, hd, -hw, 0, -hd, // left
        ]);
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        return geometry;
    }

    /** Remove the preview box and reset the add-box gesture state. */
    #clearBoxPreview() {
        if (this.#boxPreview) {
            this.#boxPreview.removeFromParent();
            this.#boxPreview.geometry.dispose();
            this.#boxPreview.material.dispose();
            this.#boxPreview = null;
        }
        this.#addBoxStage = 0;
        this.#addBoxPointerId = null;
        this.#boxDownScreen = null;
        this.#boxHeight = 0;
        this.#addBoxOrthoAxis = null;
        this.#addBoxOrthoAnchor = 0;
        this.#addBoxOrthoDirection = 1;
    }

    /**
     * Compute the two opposite local corners, raise the newbox event, and reset. In an
     * Orthographic view the hidden dimension defaults to a fixed size instead of coming from
     * the height-drag stage (which only Perspective uses).
     */
    #finalizeBox() {
        const start = this.#boxStartLocal, end = this.#boxEndLocal;
        const axis = this.#addBoxOrthoAxis;
        const hiddenAxis = axis ?? "y";
        const direction = hiddenAxis === "y" ? 1 : this.#addBoxOrthoDirection;
        const hiddenSize = axis !== null ? PlanogramContainer.#DEFAULT_BOX_SIZE : this.#boxHeight;

        const min = new THREE.Vector3(Math.min(start.x, end.x), Math.min(start.y, end.y), Math.min(start.z, end.z));
        const max = new THREE.Vector3(Math.max(start.x, end.x), Math.max(start.y, end.y), Math.max(start.z, end.z));
        const hiddenNear = start[hiddenAxis];
        const hiddenFar = hiddenNear + direction * hiddenSize;
        min[hiddenAxis] = Math.min(hiddenNear, hiddenFar);
        max[hiddenAxis] = Math.max(hiddenNear, hiddenFar);

        this.#clearBoxPreview();
        this.raiseNewBoxEvent(min, max);
    }

    /**
     * Applies the shared drag modifiers to the footprint's end corner: shift locks it to the
     * dominant free axis, ctrl snaps it to the grid. The hidden axis (if any) is re-pinned to
     * the anchor afterwards since it isn't meant to move during the drag.
     */
    #constrainBoxEnd() {
        const renderer = PlanogramRenderer.get(this);
        const hiddenAxis = this.#addBoxOrthoAxis;
        if (renderer?.ctrlKey) {
            this.#snapToGrid(this.#boxEndLocal);
            if (hiddenAxis) this.#boxEndLocal[hiddenAxis] = this.#boxStartLocal[hiddenAxis];
        }
        const [axisA, axisB] = hiddenAxis === "x" ? ["y", "z"] : hiddenAxis === "z" ? ["x", "y"] : ["x", "z"];
        if (renderer?.shiftKey) {
            const da = Math.abs(this.#boxEndLocal[axisA] - this.#boxStartLocal[axisA]);
            const db = Math.abs(this.#boxEndLocal[axisB] - this.#boxStartLocal[axisB]);
            if (da >= db) this.#boxEndLocal[axisB] = this.#boxStartLocal[axisB];
            else this.#boxEndLocal[axisA] = this.#boxStartLocal[axisA];
        }
    }

    /** Snaps the box height to the grid step when ctrl is held. */
    #constrainBoxHeight(height) {
        if (!PlanogramRenderer.get(this)?.ctrlKey) return height;
        const step = this.#gridSnapStep(this.#boxEndLocal);
        return Math.round(height / step) * step;
    }

    /**
     * Continues the footprint drag on the current gesture's plane: the horizontal floor for
     * Perspective and the Top/Bottom Orthographic views, or one of the two vertical planes for
     * the Front/Back/Left/Right Orthographic views.
     * @param {number} x
     * @param {number} y
     * @param {THREE.Vector3} out
     * @returns {boolean}
     */
    #raycastAddBoxPlane(x, y, out) {
        const axis = this.#addBoxOrthoAxis;
        return (axis === "x" || axis === "z")
            ? this.#raycastAxisPlaneLocal(x, y, axis, this.#addBoxOrthoAnchor, out)
            : this.#raycastFloorLocal(x, y, out);
    }

    /** @returns {boolean} true if the event was consumed by the add-box gesture */
    #addBoxPointerDown(x, y, pointerId) {
        if (this.#addBoxStage === 0) {
            const resolved = this.#resolveAddBoxStart(x, y);
            if (!resolved) return true;

            this.#addBoxOrthoAxis = resolved.axis;
            this.#addBoxOrthoDirection = resolved.direction;
            if (resolved.axis === "x" || resolved.axis === "z") this.#addBoxOrthoAnchor = resolved.anchorWorld;
            else this.#boxFloorWorldY = resolved.anchorWorld;

            this.#boxStartLocal.copy(resolved.start);
            this.#boxEndLocal.copy(resolved.start);
            this.#addBoxStage = 1;
            this.#addBoxPointerId = pointerId;
            this.#boxDownScreen = new THREE.Vector2(x, y);
            this.#ensureBoxPreview();
            this.#updateBoxPreview();
            return true;
        }
        if (this.#addBoxStage === 2) {
            this.#boxHeight = this.#constrainBoxHeight(this.#raycastHeightLocal(x, y));
            this.#finalizeBox();
            return true;
        }
        return true;
    }

    #addBoxPointerMove(x, y) {
        if (this.#addBoxStage === 1) {
            if (this.#raycastAddBoxPlane(x, y, this.#boxEndLocal)) {
                this.#constrainBoxEnd();
                this.#updateBoxPreview();
            }
        } else if (this.#addBoxStage === 2) {
            this.#boxHeight = this.#constrainBoxHeight(this.#raycastHeightLocal(x, y));
            this.#updateBoxPreview();
        }
    }

    #addBoxPointerUp(x, y) {
        if (this.#addBoxStage === 1) {
            // A click without a real drag is not a footprint - cancel the gesture.
            if (PlanogramContainer.isWithinThreshold(new THREE.Vector2(x, y), this.#boxDownScreen)) {
                this.#clearBoxPreview();
                return;
            }
            if (this.#raycastAddBoxPlane(x, y, this.#boxEndLocal)) {
                this.#constrainBoxEnd();
                this.#updateBoxPreview();
            }
            if (this.#addBoxOrthoAxis === null) {
                // Perspective: lock the footprint and switch to the height-drag stage.
                this.#addBoxStage = 2;
            } else {
                // Orthographic: only one dimension pair is drawable, so finalise immediately
                // with the hidden dimension at its default size.
                this.#finalizeBox();
            }
        }
    }

    //#endregion

    /**
     * Handle pointer down - start divider drag if applicable
     * @param {number} x
     * @param {number} y
     * @param {number} pointerId
     */
    onPointerDown(x, y, pointerId) {
        if (this.addBox) return this.#addBoxPointerDown(x, y, pointerId);
        // Check if we're over a draggable divider
        const result = this.overObject(x, y, false);
        const blocking = this.querySelector("planogram-blocking");
        if (blocking && !blocking.disabled && result && result.object instanceof PlanogramBlockingDivider) {
            // Start pointer-based divider drag
            this.#pointerDragging = true;
            this.#activePointerId = pointerId;
            this.#pointerDragTarget = result.object;

            // Initialize drag plane and offset (same as existing drag logic)
            const translatedCursor = this.#getTranslatedCursor(x, y, true);
            this.#raycaster.setFromCamera(translatedCursor, this.focusedCamera.camera);
            const group = this.#pointerDragTarget.group;

            this.#plane.setFromNormalAndCoplanarPoint(
                this.focusedCamera.camera.getWorldDirection(this.#plane.normal),
                this.#worldPosition.setFromMatrixPosition(group.matrixWorld)
            );

            if (this.#raycaster.ray.intersectPlane(this.#plane, this.#intersection)) {
                this.#inverseMatrix.copy(this.group.matrixWorld).invert();
                this.#offset.copy(this.#intersection).sub(this.#worldPosition.setFromMatrixPosition(group.matrixWorld));

                // Calculate divider-specific drag offset
                const planBox = this.planogramBox.clone();
                const planSize = planBox.getSize(new THREE.Vector3());
                const planMin = planBox.min.clone();
                const divider = this.#pointerDragTarget;
                const dividerCenterLocal = planMin.clone().add(divider.getPosition(planSize));
                const pointerLocal = this.#intersection.clone().sub(this.#offset).applyMatrix4(this.#inverseMatrix);
                this.#dividerDragOffset = pointerLocal.clone().sub(dividerCenterLocal);

                // Create visual preview
                this.#createDividerOutline(divider);
            }

            // Prevent default to avoid triggering drag-and-drop
            return true;
        }

        return false;
    }

    /**
     * Handle pointer move - update divider position if dragging
     * @param {number} x 
     * @param {number} y 
     * @param {number} pointerId 
     */
    onPointerMove(x, y, pointerId) {
        if (this.addBox) { this.#addBoxPointerMove(x, y); return; }
        if (!this.#pointerDragging || pointerId !== this.#activePointerId || !this.#pointerDragTarget) {
            return;
        }

        // Update drag position using raycasting
        const translatedCursor = this.#getTranslatedCursor(x, y, true);
        this.#raycaster.setFromCamera(translatedCursor, this.focusedCamera.camera);

        if (this.#raycaster.ray.intersectPlane(this.#plane, this.#intersection)) {
            this.#dragVector = this.#intersection
                .sub(this.#offset)
                .applyMatrix4(this.#inverseMatrix);

            this.setDividerPosition();
        }
    }

    /**
     * Handle pointer up - commit divider drag
     * @param {number} x 
     * @param {number} y 
     * @param {number} pointerId 
     */
    onPointerUp(x, y, pointerId) {
        if (this.addBox) { this.#addBoxPointerUp(x, y); return; }
        if (!this.#pointerDragging || pointerId !== this.#activePointerId) {
            return;
        }

        // Commit the divider position change
        this.#removeDividerOutline(true);

        // Reset pointer drag state
        this.#pointerDragging = false;
        this.#activePointerId = null;
        this.#pointerDragTarget = null;
        this.#dividerDragOffset = null;
    }

    /**
     * Handle pointer cancel - abort divider drag
     * @param {number} pointerId 
     */
    onPointerCancel(pointerId) {
        if (this.addBox) { this.#clearBoxPreview(); return; }
        if (!this.#pointerDragging || pointerId !== this.#activePointerId) {
            return;
        }

        // Cancel without committing
        this.#removeDividerOutline(false);

        // Reset pointer drag state
        this.#pointerDragging = false;
        this.#activePointerId = null;
        this.#pointerDragTarget = null;
        this.#dividerDragOffset = null;
    }

    //#endregion

    //#region Static Methods
    /**
     * 
     * @param {PlanogramObject|PlanogramProduct} element 
     * @returns {PlanogramContainer|null}
     */
    static get(element) {
        if (!element) return;
        var parent = element.parentElement;
        while (parent !== null && !(parent instanceof PlanogramContainer)) {
            parent = parent.parentElement;
        }
        return parent;
    }

    /**
   * 
   * @param {THREE.Vector2} up 
   * @param {THREE.Vector2} down 
   * @param {THREE.Vector2} threshold  
   * @returns {boolean}
   */
    static isWithinThreshold(up, down, threshold = new THREE.Vector2(3, 3)) {
        if (!up || !down) return false;
        return up.x >= (down.x - threshold.x) && up.x <= (down.x + threshold.x) &&
            up.y >= (down.y - threshold.y) && up.y <= (down.y + threshold.y);
    }

    /**
     * Build a per-segment "lineDistance" attribute so each disjoint edge starts its dash at 0.
     * THREE's built-in LineSegments.computeLineDistances accumulates distance across the whole
     * buffer, which leaves each box edge starting mid-dash with corners unaligned.
     * @param {THREE.BufferGeometry} geometry non-indexed edge geometry (vertex pairs)
     */
    static #resetSegmentLineDistances(geometry) {
        const position = geometry.attributes.position;
        const distances = new Float32Array(position.count);
        const a = new THREE.Vector3(), b = new THREE.Vector3();
        for (let i = 0; i < position.count; i += 2) {
            a.fromBufferAttribute(position, i);
            b.fromBufferAttribute(position, i + 1);
            distances[i] = 0;
            distances[i + 1] = a.distanceTo(b);
        }
        geometry.setAttribute('lineDistance', new THREE.BufferAttribute(distances, 1));
    }

    //#endregion

    //#region Event Raise Methods

    raiseClickEvent(x, y, rightClick = false) {
        const renderer = PlanogramRenderer.get(this);
        const rect = renderer.domElement.getBoundingClientRect();
        const clickEvent = new MouseEvent("click", { bubbles: true, cancelable: true, view: window, clientX: rect.x + x, clientY: rect.y + y });
        Object.defineProperty(clickEvent, 'button', { ...Object.getOwnPropertyDescriptor(clickEvent, 'button'), get: (function () { return rightClick ? 2 : 0; }).bind(this) });
        Object.defineProperty(clickEvent, 'target', { ...Object.getOwnPropertyDescriptor(clickEvent, 'target'), get: (function () { return this; }).bind(this) });
        if (this.dispatchEvent(clickEvent) && renderer.showMessages) {
            console.groupCollapsed("Empty Selection" + (rightClick ? " and Context Menu" : ""));
            console.log("Target: ", this);
            console.log("Event: ", clickEvent);
            console.groupEnd();
        }
    }

    /**
     * @param {number} x
     * @param {number} y
     */
    raiseContextMenuEvent(x, y) {
        if (this.#getFirstIntersection(x, y)) return;

        const camera = this.focusedCamera?.camera;
        if (!camera) return;

        const worldPoint = camera.getWorldPosition(new THREE.Vector3())
            .add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(PlanogramContainer.PasteDistanceFromCamera));
        this.#inverseMatrix.copy(this.group.matrixWorld).invert();
        const local = worldPoint.applyMatrix4(this.#inverseMatrix);

        const renderer = PlanogramRenderer.get(this);
        const rect = renderer.domElement.getBoundingClientRect();
        const event = new CustomEvent(PlanogramContainer.customEvents.ContextMenu, {
            bubbles: true, cancelable: true,
            detail: { clientX: rect.x + x, clientY: rect.y + y, spatialX: local.x, spatialY: local.y, spatialZ: local.z }
        });
        if (this.dispatched(event) && renderer.showMessages) {
            console.groupCollapsed("Context Menu (Empty Space)");
            console.log("Spatial: ", local);
            console.groupEnd();
        }
    }

    /**
     * Raise the "newbox" relay event with the two opposite corners of a drawn box,
     * expressed in planogram-local coordinates.
     * @param {THREE.Vector3} min
     * @param {THREE.Vector3} max
     */
    raiseNewBoxEvent(min, max) {
        const renderer = PlanogramRenderer.get(this);
        const event = new CustomEvent(PlanogramContainer.customEvents.NewBox, {
            bubbles: true, cancelable: true,
            detail: { x1: min.x, y1: min.y, z1: min.z, x2: max.x, y2: max.y, z2: max.z }
        });
        if (this.dispatched(event) && renderer.showMessages) {
            console.groupCollapsed("New Box Drawn");
            console.log("Min: ", min);
            console.log("Max: ", max);
            console.groupEnd();
        }
    }

    dispatched(event) {
        const renderer = PlanogramRenderer.get(this)?.showMessages ?? false;
        const dropped = this.dispatchEvent(event);
        return dropped && renderer;
    }

    //#endregion

    //#region WebComponent Lifecycle CallBacks
    connectedCallback() {
        this.#setUpScene();
        PlanogramRenderer.applyToRelay(this, PlanogramContainer.customEvents);
        this.#firstRender = false;
        const frames = [...PlanogramRenderer.get(this).querySelectorAll('planogram-camera-frame')];
        const associatedFrame = frames.find(f => f.uid === this.groupFrameId);
        if (associatedFrame && associatedFrame instanceof PlanogramCameraFrame)
            associatedFrame.addCameras();
        [...this.querySelectorAll('planogram-blocking')].forEach(x => x.applyShowPositionsThroughBlocking?.(this.showPositionsThroughBlocking));
        [...this.querySelectorAll('planogram-position')].forEach(x => { if (x.transparencyChanged) x.transparencyChanged(); });
        [...this.querySelectorAll('planogram-text')].forEach(x => x.applyShowPositionsThroughBlocking?.(this.showPositionsThroughBlocking));
        [...this.querySelectorAll('planogram-label')].forEach(x => x.applyShowPositionsThroughBlocking?.(this.showPositionsThroughBlocking));

        const toolBar = document.createElement("slot");
        toolBar.name = "toolbar";
        toolBar.slot = "toolbar-frame1";
        this.shadowRoot.appendChild(toolBar);

        this.#notifyCamerasReady();

        if (this.componentTransform) this.refreshRotationGizmo();
    }

    /**
     * Notify all cameras associated with this container that it's ready.
     * Handles the case where cameras load before the container.
     */
    #notifyCamerasReady() {
        if (!this.containerId) return;

        const renderer = PlanogramRenderer.get(this);
        if (!renderer) return;

        const cameras = [...renderer.querySelectorAll(`planogram-camera[container-id='${this.containerId}']`)];
        cameras.forEach(camera => {
            // Use queueMicrotask to avoid blocking the main thread
            queueMicrotask(() => {
                if (camera.onContainerReady) {
                    camera.onContainerReady();
                }
            });
        });
    }

    disconnectedCallback() {
        if (this.#resizing) this.endResize(false);
        if (this.#rotating) this.endRotation(false);
        this.#clearRotationGizmo();
        this.#scene.removeFromParent();
        this.#scene = null;
        this.#removeDividerOutline(false);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (this.#firstRender || oldValue === newValue) return;
        if (name === PlanogramContainer.Attributes.HideChestWalls) {
            [...this.querySelectorAll("planogram-component")].forEach(x => x.subComponentPlug(true));
        } else if (name === PlanogramContainer.Attributes.ShowPositionsThroughBlocking) {
            [...this.querySelectorAll('planogram-blocking')].forEach(x => x.applyShowPositionsThroughBlocking?.(this.showPositionsThroughBlocking));
            [...this.querySelectorAll('planogram-position')].forEach(x => { if (x.transparencyChanged) x.transparencyChanged(); });
            [...this.querySelectorAll('planogram-text')].forEach(x => x.applyShowPositionsThroughBlocking?.(this.showPositionsThroughBlocking));
            [...this.querySelectorAll('planogram-label')].forEach(x => x.applyShowPositionsThroughBlocking?.(this.showPositionsThroughBlocking));
        } else if (name === PlanogramContainer.Attributes.ComponentTransform) {
            // End any active gesture without committing before rebuilding/clearing the transform UI.
            if (!this.componentTransform) {
                if (this.#resizing) this.endResize(false);
                if (this.#rotating) this.endRotation(false);
            }
            this.refreshRotationGizmo();
        } else if (name === PlanogramContainer.Attributes.AddBox) {
            // Abort any in-progress box gesture when the mode is turned off.
            if (!this.addBox) this.#clearBoxPreview();
        }
    }
    //#endregion


}